/**
 * Employees.tsx — F10 T10.9 (tenant scope) — kartoteka kadrowa.
 *
 * Zgodne z `docs/plan.md §1528-1533`:
 *   - Tabela: imię nazwisko, stanowisko, dział, konto tak/nie, status zatrudnienia.
 *   - Modal: 3 zakładki (Dane osobowe / Zatrudnienie / [opc.] Utwórz konto —
 *     atomowy POST /api/employees z `createUser` per §1327).
 *   - Szczegóły: 3 zakładki (Dane osobowe / Zatrudnienie / Adresy) + dane
 *     wrażliwe (PESEL/dowód) jako sekcja w „Dane osobowe", widoczne plain dla
 *     osób z permission `hr.employees.read_pesel` (gate po stronie backendu).
 *   - Akcja „Utwórz konto dla tego pracownika" (dla employee bez user'a).
 *   - Akcja „Anonimizuj (RODO)" — z confirmation, audit (single confirm —
 *     plan §1533 nie wymaga double-confirm; PHASE_10_PLAN dorzuciło double-
 *     confirm bez podstawy w planie zarządu).
 *
 * Endpointy:
 *   GET    /api/employees                            lista
 *   GET    /api/employees/:id                        szczegóły (z PESEL gdy permission)
 *   POST   /api/employees                            create — atomowy z opcjonalnym createUser
 *   DELETE /api/employees/:id                        soft-delete
 *   POST   /api/employees/:id/restore                przywróć z kosza
 *   POST   /api/employees/:id/create-account         dorzuć konto user do istniejącego employee
 *   POST   /api/employees/:id/anonymize              RODO art. 17
 *   GET    /api/companies/:companyId/org-units       lista jednostek organizacyjnych (filtr + modal)
 *   GET    /api/addresses?ownerType=employee&ownerId=:id   adresy pracownika
 *   POST   /api/addresses                            create address (ownerType=employee)
 *   PATCH  /api/addresses/:id                        edit address
 *   DELETE /api/addresses/:id                        soft-delete address
 *   GET    /api/countries                            słownik krajów (ISO + nazwy)
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  EMPLOYEE_ID_DOCUMENT_TYPES,
  type EmployeeIdDocumentType,
  EMPLOYMENT_TYPES,
  type EmploymentType,
  RECORD_LANGUAGES,
  type RecordLanguage,
} from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  AlertTriangle,
  CalendarDays,
  KeyRound,
  Lock,
  MapPin,
  MoreHorizontal,
  Network,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import {
  type ColumnDefinition,
  type ColumnFilters,
  type ColumnHeaderFilter,
  type ColumnHeaderFilters,
  ConfigurableTable,
  ListActionsMenu,
  ListBuilderMenu,
  ListFilterBar,
  ListTopWidgets,
  ListViewModeToggle,
  QuickFilterBar,
  useListViewMode,
  useTableLayout,
} from '@/components/configurable-table';
import { CustomRowMenuItems } from '@/components/configurable-table/CustomActionButtonBar';
import { COMPOSITE_TEXT_FILTER_OPERATORS } from '@/components/configurable-table/filter-row/filter-operators';
import type { RowActionItem } from '@/components/configurable-table/RowActionsMenu';
import { useServerListControls } from '@/components/configurable-table/useServerListControls';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DatePicker } from '@/components/DatePicker';
import { Drawer } from '@/components/Drawer';
import { Dropdown } from '@/components/Dropdown';
import { CreateAccountForExistingModal } from '@/components/employees/CreateAccountForExistingModal';
import { FormField } from '@/components/FormField';
import { GroupChipBar } from '@/components/groups/GroupChipBar';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { formatDictionaryOptionLabel, useDictionary } from '@/hooks/useDictionary';
import { useForcedListLayoutId } from '@/hooks/useForcedListLayoutId';
import { useFormDraft } from '@/hooks/useFormDraft';
import { useReportRecordLock } from '@/hooks/useReportRecordLock';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { api } from '@/lib/api';
import {
  employeeIdDocumentTypeLabel,
  employmentTypeLabel,
  recordLanguageLabel,
} from '@/lib/core-field-option-labels';
import {
  buildCategoryColumn,
  buildCompanyColumn,
  buildCoreColumns,
  buildExtColumns,
  computeVisibleColumnParams,
  type CoreColumnOverride,
  type ExtRefLabels,
  FieldCatalogErrorState,
  useExtRefLabelsGetter,
  useSystemModuleFields,
} from '@/lib/ext-list-columns';
import { useListActions } from '@/lib/list-actions/useListActions';
import { useEngineListExport } from '@/lib/list-export/useEngineListExport';
import { companyIdParam, useActiveCompanyScope } from '@/lib/use-active-company';
import { useMyModules } from '@/lib/use-my-modules';
import { useCompanies, useRoleTemplates } from '@/lib/use-permissions';
import {
  buildEmployeesListParams,
  buildIndentedOrgUnitOptions,
  EMPLOYEE_DETAIL_KEY,
  type EmployeeDetail,
  type EmployeeListItem,
  EMPLOYEES_LIST_KEY,
  type EmployeesListFilters,
  ORG_UNITS_BY_COMPANY_KEY,
  useEmployeeDetail,
  useEmployeesList,
  useOrgUnitsForCompany,
} from '@/lib/use-tenant-people';

const ADDRESS_KINDS = ['registered', 'billing', 'shipping', 'mailing', 'correspondence'] as const;

type AddressKind = (typeof ADDRESS_KINDS)[number];
type DrawerTab = 'personal' | 'employment' | 'addresses';
type CreateModalTab = 'personal' | 'employment' | 'account';
type EditModalTab = 'personal' | 'employment';

interface AddressRow {
  id: string;
  ownerType: string;
  ownerId: string;
  kind: string;
  label: string | null;
  street: string | null;
  buildingNo: string | null;
  apartmentNo: string | null;
  postalCode: string | null;
  city: string | null;
  region: string | null;
  countryCode: string | null;
  isPrimary: boolean;
  isActive: boolean;
  notes: string | null;
}

interface CountryRow {
  code: string;
  namePl: string;
  nameEn: string;
}

/**
 * Slugi rdzenne obsługiwane OSOBNO (dedykowany builder poza `buildCoreColumns`), więc pomijane
 * przez katalog — inaczej powstałaby zdublowana kolumna:
 *  - `company_id` — dedykowany `buildCompanyColumn` (id `company`).
 *
 * `first_name`/`middle_name` NIE są wykluczane — mają `showInList=true` i wchodzą przez katalog
 * jako zwykłe, domyślnie WIDOCZNE kolumny (`defaultVisible: true`, patrz `EMPLOYEE_OVERRIDES`,
 * 2026-08-27: parytet z „Nazwa skrócona" w Firmach). Wartość dubluje się ze złożoną kolumną
 * „Nazwisko Imię" (nadpisanie `last_name`) — świadomie zaakceptowane. Sklejka i tak dostaje
 * imię/drugie imię przez `companions` niżej, niezależnie od widoczności tych kolumn.
 *
 * `is_active` NIE jest już tutaj — backend flagnął pole `showInList=true` i jest teraz
 * zwykłą, filtrowalną kolumną boolean „Aktywny" generowaną przez katalog (odznaka
 * „Archiwum" w komórce nazwiska ZOSTAJE — to dodatkowy sygnał wizualny, nie duplikat
 * filtra, patrz `last_name` override niżej).
 */
const EMPLOYEE_EXCLUDE_SLUGS: ReadonlySet<string> = new Set(['company_id']);

/**
 * Kolejność DOSŁOWNA (parytet ze starym UX) 4 kolumn rdzennych, których pozycja jest jawnie
 * ustalona — niezależna od `sortOrder` katalogu (patrz komentarz przy `columns` w komponencie).
 */
const KNOWN_CORE_ORDER = ['last_name', 'position_code', 'org_unit_id', 'employment_type'];

/**
 * Id kolumny wirtualnej „Konto" (`hasAccount`, poza katalogiem pól). Jej filtr boolean NIE
 * jest routowany przez `headerFilters` (backend nie zna sluga tej kolumny — computed,
 * cichy no-op), tylko osobno przez `?hasAccount=` (patrz `hasAccountFilter` w komponencie).
 * Stała współdzielona między budową kolumny i `serializedHeaderFilters`, żeby wykluczenie
 * nie rozjechało się w dwóch miejscach.
 */
const ACCOUNT_COLUMN_ID = 'account';

// ============================================================================
// Helpers
// ============================================================================

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
}

function handleApiError(
  err: unknown,
  t: ReturnType<typeof useTranslation>['t'],
  toast: ReturnType<typeof useToast>,
  fallbackKey: string,
): void {
  if (isAxiosError(err)) {
    const code = err.response?.data?.error as string | undefined;
    if (code) {
      const i18nKey = `tenant.employees.toast.errors.${code}`;
      const msg = t(i18nKey, { defaultValue: '' });
      if (msg) {
        toast.show({ variant: 'error', title: msg });
        return;
      }
    }
  }
  toast.show({ variant: 'error', title: t(fallbackKey) });
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// ============================================================================
// Strona
// ============================================================================

export function Employees() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { me } = useTenantAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // Skrót „Grafik" tylko z modułem „Grafiki i RCP" (`grafiki`) — bez niego `/grafik` pokazuje
  // planszę „moduł nieaktywny" (ModuleGuard na trasie). Ta sama reguła co widok „Kadry" w sidebarze.
  const myModules = useMyModules();
  const hasScheduleModule =
    myModules.data?.subscriptions?.some((s) => s.code === 'grafiki') ?? false;

  // Przełącznik „kosza" (gcrecord) zdjęty z kartotek (PHASE 25): „Usuń" = miękkie usunięcie
  // bez widoku odzyskiwania w UI. Archiwum (flaga) jest wyłącznie na dokumentach.
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  /**
   * Filtr chipów nad listą = GRUPY ze słownika `employee_groups` (decyzja 2026-08-18).
   * Wiele grup naraz, semantyka OR („ma którąkolwiek z").
   */
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

  // Aktywna firma (CompanySwitcher) — listing scope'owany po stronie backendu
  // przez `?companyId=` (Etap 5, `useActiveCompanyScope`/`companyIdParam`).
  // Brak (tryb "Wszystkie firmy") => unia po widocznych firmach (Model B),
  // bezpieczne od momentu naprawy backendu (`resolveListCompanyScope`).
  const companyScope = useActiveCompanyScope();
  const companyId = companyIdParam(companyScope);

  // Filtry per kolumna z popupu w nagłówku + wiersz „Szukaj…" pod nagłówkami.
  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 300);

  // Wymuszony layout listy z pozycji Kreatora menu, z której user przyszedł
  // (kartoteka-layout-per-menu) — ta sama zmienna `tableId` musi iść do WSZYSTKICH
  // konsumentów niżej (ListTopWidgets, ListFilterBar, ConfigurableTable), inaczej
  // widżety i tabela renderują dwa różne layouty.
  const forcedListLayoutId = useForcedListLayoutId('employees');
  const tableId = forcedListLayoutId ? `employees::layout-${forcedListLayoutId}` : 'employees';

  // Dokleja `?mi=<id>` do nawigacji w głąb (nowy/detal), żeby formularz pracownika
  // znał kontekst pozycji menu (viewRef) — URL detalu jest osobną nawigacją, nie
  // dziedziczy query stringa listy.
  const withMenuItem = useWithMenuItem();

  // Szybkie filtry (presety z list_config) — chipy nad listą.
  const { layout: listLayout, isLoading: layoutLoading } = useTableLayout(tableId);
  const quickFilters = useMemo(
    () => listLayout?.layoutConfig?.quickFilters ?? [],
    [listLayout?.layoutConfig?.quickFilters],
  );
  const [activeQuickFilterId, setActiveQuickFilterId] = useState<string | null>(null);
  // Tryb widoku sterowany na poziomie strony — przełącznik w nagłówku (spójnie z
  // Kontrahentami i modułami Kreatora). ConfigurableTable dostaje go jako prop i
  // wygasza własny wbudowany pasek przełącznika. Pastylki = tryby dostępne w bieżącym
  // layoucie (raport tabeli, m.in. kalendarz tylko z konfiguracją) — wspólny hook.
  const viewModes = useListViewMode();
  useEffect(() => {
    if (activeQuickFilterId === null && quickFilters.length > 0) {
      const def = quickFilters.find((q) => q.isDefault);
      if (def) setActiveQuickFilterId(def.id);
    }
  }, [quickFilters, activeQuickFilterId]);

  const companiesQ = useCompanies();
  const companies = useMemo(() => companiesQ.data?.companies ?? [], [companiesQ.data?.companies]);
  // Słownik `employee_positions` (F24 Pakiet 0) — mapa kod→etykieta dla kolumny/filtra
  // „Stanowisko" na liście (backend zwraca surowy kod, nie etykietę).
  const positionsDict = useDictionary('employee_positions');
  const positionLabelByCode = useMemo(() => {
    const map = new Map<string, string>();
    if (!positionsDict.dictionary) return map;
    for (const item of positionsDict.items) {
      map.set(item.code, formatDictionaryOptionLabel(item, positionsDict.dictionary.displayMode));
    }
    return map;
  }, [positionsDict.dictionary, positionsDict.items]);
  // Mapa id→pracownik dla renderu kolumny „Przełożony" (analogicznie do `orgUnitsById`) —
  // osobne zapytanie listy pracowników w zakresie aktywnej firmy (mirror `OrgChart.tsx`:
  // `useEmployeesList({ companyId, limit })`), bez filtrów/paginacji/sortu strony listy.
  // Limit 200 = TWARDY cap silnika list (`typed-executor.ts` przycina do 200; wyższa wartość jest
  // bez znaczenia). Przełożony spoza zakresu (firma inna niż aktywnie wybrana, albo >200
  // pracowników w firmie posortowanych po nazwisku) wyrenderuje się jako „—" — ta sama akceptowana
  // granica co `orgUnitsById` (patrz ADR-0095 „Poza zakresem").
  const managersQ = useEmployeesList({ companyId, limit: 200 });
  const employeesById = useMemo(
    () => new Map((managersQ.data?.employees ?? []).map((e) => [e.id, e] as const)),
    [managersQ.data?.employees],
  );
  // `me.companies` (TenantCompany: {id, name}) — źródło opcji multi-choice dla kolumny
  // „Firma" (`buildCompanyColumn`). Osobna nazwa od `companies` wyżej (`useCompanies()`
  // → CompanyListItem: {id, legalName, shortName}, używane w modalach Edit/Create) — inny
  // kształt, nie podmieniamy istniejącego użycia.
  const meCompanies = useMemo(() => me?.companies ?? [], [me?.companies]);

  // Katalog pól modułu `employees` (core + ext) — źródło WSZYSTKICH kolumn (rdzennych i
  // dodatkowych) generowanych dynamicznie poniżej.
  const fieldsQ = useSystemModuleFields('employees');
  // `getRefLabels` — getter stabilny (patrz `useExtRefLabelsGetter`), aktualizowany
  // niżej PO `employeesQ`.
  const [getRefLabels, setRefLabels] = useExtRefLabelsGetter();
  const extColumns = useMemo<ColumnDefinition<EmployeeListItem>[]>(
    () => buildExtColumns<EmployeeListItem>(fieldsQ.data ?? [], t, getRefLabels),
    [fieldsQ.data, t, getRefLabels],
  );

  // Sort serwerowy + paginacja (silnik list) — wspólny hook, patrz useServerListControls.ts
  // (parytet z Contractors.tsx). `sortableSlugs` — `meta.sortable` z ostatniej odpowiedzi
  // listy (`employeesQ` niżej), zasilane PO zapytaniu przez EFEKT (analogicznie do
  // `getRefLabels`/`setRefLabels`).
  const controls = useServerListControls();
  const [sortableSlugs, setSortableSlugs] = useState<string[] | undefined>(undefined);
  const [filterableSlugs, setFilterableSlugs] = useState<string[] | undefined>(undefined);

  // Zmiana zakresu firmy (CompanySwitcher) zawęża wynik — reset strony jak przy filtrach/sorcie
  // (inaczej user może utknąć na pustej stronie N).
  useEffect(() => {
    controls.resetPage();
  }, [companyId, controls.resetPage]);

  // Zmiana filtrów w nagłówku wraca na pierwszą stronę — DWA callsite'y (ListFilterBar +
  // ConfigurableTable), więc zawinięte w jeden stabilny handler (jak w Contractors.tsx).
  const handleHeaderFiltersChange = useCallback(
    (next: ColumnHeaderFilters) => {
      setHeaderFilters(next);
      controls.resetPage();
    },
    [controls.resetPage],
  );

  // Rejestr nadpisań kolumn rdzennych (per-kartoteka) — render bogaty tam, gdzie generyk
  // (typ→komórka) nie odtwarza wiernie stary render: kompozytowa „Imię i nazwisko" (link +
  // odznaki archiwum/skasowany), „Jednostka" (nazwa z projekcji serwera `orgUnitName` +
  // serwerowy sort/filtr), „Typ zatrudnienia" (plakietka niebieska). Budowany WEWNĄTRZ
  // komponentu (`useMemo`) — domyka `withMenuItem`, którego rejestr modułowy (poza komponentem)
  // by nie widział.
  const EMPLOYEE_OVERRIDES = useMemo<Record<string, CoreColumnOverride<EmployeeListItem>>>(
    () => ({
      last_name: ({ field, resolveSortable }) => ({
        id: field.slug,
        accessor: (row) =>
          `${row.lastName} ${row.firstName}${row.middleName ? ` ${row.middleName}` : ''}`,
        header: t('tenant.employees.col.name'),
        defaultVisible: true,
        defaultWidth: 260,
        hideable: false,
        sortable: resolveSortable?.(field) ?? false,
        // Filtr kolumny ZŁOŻONEJ „Imię i nazwisko" — grupa OR: syntetyczny slug `full_name`
        // (backend rozwija na `first_name`/`last_name`: OR dla operatorów twierdzących, AND dla
        // przeczących — patrz `headerFilterOrGroups`). `conditionOnly` — slug filtra-tylko, spoza
        // `/distinct`. `orGroupFields` napędza wzajemne wygaszanie z osobną kolumną „Imię"
        // (slug `first_name`): aktywny filtr tu wygasza tamten i odwrotnie.
        headerFilter: {
          category: 'text',
          fieldSlug: 'full_name',
          conditionOnly: true,
          operators: COMPOSITE_TEXT_FILTER_OPERATORS,
          orGroupFields: ['first_name', 'last_name'],
        },
        renderCell: (_value, row) => {
          const isDeleted = row.gcrecord !== null;
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <Link
                to={withMenuItem(`/employees/${row.id}`)}
                className={`font-medium hover:underline ${
                  isDeleted ? 'text-text-tertiary' : 'text-accent-700 dark:text-accent-300'
                }`}
              >
                {row.lastName} {row.firstName}
                {row.middleName ? ` ${row.middleName}` : ''}
              </Link>
              {(!row.isActive || isDeleted) && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {!row.isActive && !isDeleted && (
                    <Badge color="gray">{t('tenant.employees.badge.archived')}</Badge>
                  )}
                  {isDeleted && <Badge color="red">{t('tenant.employees.badge.deleted')}</Badge>}
                </div>
              )}
            </div>
          );
        },
      }),
      // Imię / drugie imię — samodzielne, domyślnie WIDOCZNE kolumny (2026-08-27, decyzja usera:
      // parytet z „Nazwa skrócona" w Firmach). Wartość dubluje się ze złożoną kolumną
      // „Nazwisko Imię" (last_name) — świadomie zaakceptowane, nie ukrywane. Filtr/sort działają
      // (oba na białej liście silnika).
      first_name: ({ field, accessor, resolveSortable }) => ({
        id: field.slug,
        accessor,
        header: field.name,
        defaultVisible: true,
        defaultWidth: 140,
        sortable: resolveSortable?.(field) ?? false,
        headerFilter: { category: 'text', fieldSlug: 'first_name' },
        renderCell: (value) =>
          value ? (
            <span className="text-text-secondary">{String(value)}</span>
          ) : (
            <span className="text-text-tertiary">—</span>
          ),
      }),
      middle_name: ({ field, accessor, resolveSortable }) => ({
        id: field.slug,
        accessor,
        header: field.name,
        defaultVisible: true,
        defaultWidth: 140,
        sortable: resolveSortable?.(field) ?? false,
        headerFilter: { category: 'text', fieldSlug: 'middle_name' },
        renderCell: (value) =>
          value ? (
            <span className="text-text-secondary">{String(value)}</span>
          ) : (
            <span className="text-text-tertiary">—</span>
          ),
      }),
      // „Jednostka organizacyjna" (F24 Pakiet 0, dawniej „Dział") — nazwa jednostki jako TEKST
      // z projekcji backendu (`orgUnitName`, LEFT JOIN `org_units` w deskryptorze, mirror kolumny
      // „Kategoria"). Render/sort/filtr po NAZWIE działają serwerowo w KAŻDYM trybie firmy — też
      // „Wszystkie firmy" (join po `id`, bez kontekstu jednej firmy). Wcześniej nazwę podstawiała
      // lokalna mapa jednostek jednej firmy (`orgUnitsById`), więc w unii było pusto i bez sortu/
      // filtra. Filtr: tekstowy `conditionOnly` (nazwa liczona z joina — pomija `/distinct`).
      org_unit_id: ({ field, resolveSortable }) => ({
        id: field.slug,
        accessor: 'orgUnitName',
        header: field.name,
        defaultVisible: true,
        defaultWidth: 180,
        sortable: resolveSortable?.(field) ?? false,
        headerFilter: { category: 'text', fieldSlug: 'org_unit_id', conditionOnly: true },
        renderCell: (value) =>
          value == null || value === '' ? (
            <span className="text-text-tertiary">—</span>
          ) : (
            <span className="text-text-secondary">{String(value)}</span>
          ),
      }),
      // „Przełożony" — referencja rdzenna BEZ resolvera etykiety, render przez lokalną mapę
      // `employeesById` (mirror `org_unit_id`). Backend NIE ma `manager_employee_id` w mapie
      // header-filterów (kolumna-tylko) — `filterable: false` (bez tego pusty `headerFilter`
      // pokazałby martwy generyczny „Szukaj…" — sama kategoria `'none'`/brak `headerFilter` tego
      // NIE tłumi, bo `FilterRowCell` sprawdza `filterable !== false`, nie kategorię, przed
      // pokazaniem legacy inputu). NIEsortowalna z tego samego powodu co „Jednostka organizacyjna".
      // F24 Pakiet 0: pole jest teraz NADPISANIEM przełożonego (puste = wynika z jednostki) —
      // render bez zmian, kolumna dalej pokazuje to co faktycznie zapisane na rekordzie.
      manager_employee_id: ({ field }) => ({
        id: field.slug,
        accessor: 'managerEmployeeId',
        header: field.name,
        defaultVisible: true,
        defaultWidth: 180,
        sortable: false,
        filterable: false,
        renderCell: (value) => {
          const m = value ? employeesById.get(String(value)) : null;
          return m ? (
            <span className="text-text-secondary">
              {m.lastName} {m.firstName}
            </span>
          ) : (
            <span className="text-text-tertiary">—</span>
          );
        },
      }),
      // „Stanowisko" (F24 Pakiet 0, dawniej wolny tekst `position`) — teraz kod pozycji
      // słownika `employee_positions`; render przez `positionLabelByCode` (etykieta, nie
      // surowy kod). Filtr tekstowy zostaje na `position_code` (szuka po KODZIE — parytet
      // z resztą kolumn tekstowych; słownik startuje pusty więc select z opcjami byłby
      // pustym dropdownem u większości tenantów).
      position_code: ({ field, accessor, resolveSortable }) => ({
        id: field.slug,
        accessor,
        header: field.name,
        defaultVisible: true,
        defaultWidth: 180,
        sortable: resolveSortable?.(field) ?? false,
        headerFilter: { category: 'text', fieldSlug: 'position_code' },
        renderCell: (value) =>
          value ? (
            <span className="text-text-secondary">
              {positionLabelByCode.get(String(value)) ?? String(value)}
            </span>
          ) : (
            <span className="text-text-tertiary">—</span>
          ),
      }),
      employment_type: ({ field, accessor, t: ctxT, resolveSortable }) => ({
        id: field.slug,
        accessor,
        header: field.name,
        defaultVisible: true,
        defaultWidth: 150,
        sortable: resolveSortable?.(field) ?? false,
        headerFilter: {
          category: 'select',
          fieldSlug: 'employment_type',
          transformLabel: (value) =>
            value
              ? ctxT(`tenant.employees.employmentType.${value}`, { defaultValue: value })
              : '(Puste)',
        },
        renderCell: (value) =>
          value ? (
            <Badge color="blue">{ctxT(`tenant.employees.employmentType.${String(value)}`)}</Badge>
          ) : (
            <span className="text-text-tertiary">—</span>
          ),
      }),
    }),
    [t, withMenuItem, employeesById, positionLabelByCode],
  );

  // Kolumny rdzenne z katalogu (klucz mechaniczny ze sluga; garstka przez rejestr nadpisań).
  // Firma wykluczona (dedykowany builder — patrz `EMPLOYEE_EXCLUDE_SLUGS`); imię/drugie imię
  // wchodzą przez katalog jako kolumny ukryte domyślnie (nadpisania `defaultVisible: false`);
  // `is_active` jest teraz zwykłą kolumną katalogu (odznaka archiwum w komórce nazwiska zostaje).
  // Builder z getterem etykiet odwołań jako parametrem — tabela dostaje etykiety z bieżącej strony
  // listy, eksport CSV (E16) etykiety z odpowiedzi eksportu (wszystkie wyeksportowane wiersze).
  const buildEmployeeCoreColumns = useCallback(
    (getLabels: () => ExtRefLabels | undefined): ColumnDefinition<EmployeeListItem>[] =>
      buildCoreColumns<EmployeeListItem>({
        fields: fieldsQ.data ?? [],
        t,
        getRefLabels: getLabels,
        overrides: EMPLOYEE_OVERRIDES,
        excludeSlugs: EMPLOYEE_EXCLUDE_SLUGS,
        resolveSortable: (f) => controls.isCoreColumnSortable(f.slug, sortableSlugs),
        resolveFilterable: (f) => controls.isCoreColumnFilterable(f.slug, filterableSlugs),
      }),
    [
      fieldsQ.data,
      t,
      EMPLOYEE_OVERRIDES,
      controls.isCoreColumnSortable,
      sortableSlugs,
      controls.isCoreColumnFilterable,
      filterableSlugs,
    ],
  );
  const coreColumns = useMemo(
    () => buildEmployeeCoreColumns(getRefLabels),
    [buildEmployeeCoreColumns, getRefLabels],
  );

  // Definicja kolumn dla ConfigurableTable — kolejność DOSŁOWNA (parytet ze starym UX,
  // niezależna od `sortOrder` katalogu): nazwisko, firma, stanowisko, dział, typ zatrudnienia,
  // konto (wirtualna), pola dodatkowe. `KNOWN_CORE_ORDER` (module scope) domyka 4 kolumny
  // rdzenne, których kolejność jest jawnie ustalona; jakikolwiek NOWY core-slug z
  // `showInList=true` (poza tymi czterema i wykluczonymi w `EMPLOYEE_EXCLUDE_SLUGS`) trafia
  // mimo to na listę — dołożony PRZED kolumną „Konto" — żeby nowe pole z katalogu nigdy nie
  // zniknęło po cichu.
  const assembleColumns = useCallback(
    (
      coreColumns: ColumnDefinition<EmployeeListItem>[],
      ext: ColumnDefinition<EmployeeListItem>[],
      getLabels: () => ExtRefLabels | undefined,
    ): ColumnDefinition<EmployeeListItem>[] => {
      const coreById = new Map(coreColumns.map((c) => [c.id, c] as const));
      const known = KNOWN_CORE_ORDER.map((id) => coreById.get(id)).filter(
        (c): c is ColumnDefinition<EmployeeListItem> => c !== undefined,
      );
      const leftoverCore = coreColumns.filter((c) => !KNOWN_CORE_ORDER.includes(c.id));
      const nameCol = coreById.get('last_name');
      const restKnown = known.filter((c) => c.id !== 'last_name');
      // „Nazwisko" — kolumna SAMODZIELNA obok złożonej „Imię i nazwisko" (2026-08-27, parytet
      // z „Imię"/„Drugie imię"). `last_name` (id/slug) jest już skonsumowany przez `nameCol`
      // (nadpisanie, link do detalu) — ten synthetic id `surname` mapuje na TEN SAM slug
      // backendu `last_name` (patrz `coreIdToSlug` niżej). Filtr po `last_name` PRZENIESIONY
      // tu z kolumny złożonej (ta straciła `headerFilter` — dwa niezależne pola na jednym
      // slugu dawały sprzeczne warunki AND); sort działa na obu (osobne wywołania
      // `resolveSortable`/`isCoreColumnSortable`, ten sam slug backendu).
      // Nagłówek z katalogu (dane), jak reszta kolumn core — NIE nowy klucz i18n. Fallback
      // `?? 'Nazwisko'` jest NIEOSIĄGALNY w praktyce: `surnameCol` powstaje tylko gdy `nameCol`
      // istnieje (linia niżej), a `nameCol` wymaga tego samego wpisu `last_name` w `fieldsQ.data`
      // co ten `.find()` — jeśli jedno się powiodło, drugie też musi.
      const lastNameFieldName =
        fieldsQ.data?.find((f) => f.slug === 'last_name')?.name ?? 'Nazwisko';
      const surnameCol: ColumnDefinition<EmployeeListItem> = {
        id: 'surname',
        accessor: 'lastName',
        header: lastNameFieldName,
        defaultVisible: true,
        defaultWidth: 160,
        sortable: controls.isCoreColumnSortable('last_name', sortableSlugs),
        headerFilter: { category: 'text', fieldSlug: 'last_name' },
        renderCell: (value) =>
          value ? (
            <span className="text-text-secondary">{String(value)}</span>
          ) : (
            <span className="text-text-tertiary">—</span>
          ),
      };
      const accountCol: ColumnDefinition<EmployeeListItem> = {
        id: ACCOUNT_COLUMN_ID,
        accessor: 'hasAccount',
        header: t('tenant.employees.col.account'),
        defaultVisible: true,
        defaultWidth: 110,
        sortable: false,
        // `has_account` jest kolumną WIRTUALNĄ (poza katalogiem, `hasAccount` liczone przez
        // backend) — NIE ma sluga w backendowej mapie header-filterów. Filtr boolean rysuje
        // się (kategoria `boolean` jest obsłużona przez `FilterRowCell` niezależnie od
        // `filterable`), ale wartość jest routowana OSOBNO do `?hasAccount=` (patrz
        // `hasAccountFilter` niżej) — `filterable: false`, żeby kolumna nigdy nie dostała
        // generycznego pola „Szukaj…" jako fallbacku.
        filterable: false,
        headerFilter: { category: 'boolean' },
        renderCell: (value) =>
          value ? (
            <Badge color="green">{t('tenant.employees.account.yes')}</Badge>
          ) : (
            <Badge color="gray">{t('tenant.employees.account.no')}</Badge>
          ),
      };
      return [
        ...(nameCol ? [nameCol, surnameCol] : []),
        // Fabryka wspólna (`@/lib/ext-list-columns`) — dokłada filtr firmy (multi-choice) +
        // `filterable:false`; `headerKey` zachowuje istniejący klucz i18n (bez zmiany widocznego
        // nagłówka). `defaultVisible` fabryki jest zawsze `true`, jak stara inline kolumna.
        buildCompanyColumn<EmployeeListItem>({
          companies: meCompanies,
          getRefLabels: getLabels,
          t,
          headerKey: 'tenant.employees.field.company',
        }),
        ...restKnown,
        ...leftoverCore,
        // Kolumna „Kategoria" — WSPÓLNY builder `buildCategoryColumn` (jak Kontrahenci i 7 klas
        // dokumentów), NIE z katalogu pól (patrz JSDoc buildera w `ext-list-columns.tsx`).
        buildCategoryColumn<EmployeeListItem>({
          t,
          resolveSortable: (slug) => controls.isCoreColumnSortable(slug, sortableSlugs),
        }),
        accountCol,
        ...ext,
      ];
    },
    [meCompanies, t, fieldsQ.data, controls.isCoreColumnSortable, sortableSlugs],
  );
  const columns = useMemo(
    () => assembleColumns(coreColumns, extColumns, getRefLabels),
    [assembleColumns, coreColumns, extColumns, getRefLabels],
  );

  // Mapa id kolumny → slug backendu dla `?columns=`. id = slug dla WSZYSTKIE kolumny rdzenne
  // (buildCoreColumns); „Firma"/„Konto"/„Nazwisko" są kolumnami spoza mechanicznego mapowania
  // (wirtualne, albo — jak `surname` — DRUGI id na TEN SAM slug co już istniejąca kolumna
  // `last_name`) — mapowane jawnie na slug backendu.
  const coreIdToSlug = useMemo(() => {
    const map: Record<string, string> = {
      company: 'company_id',
      account: 'has_account',
      surname: 'last_name',
      category: 'category',
    };
    for (const c of coreColumns) map[c.id] = c.id; // id = slug (buildCoreColumns)
    return map;
  }, [coreColumns]);

  // Slugi aktualnie widocznych kolumn (wspólny helper) → `?columns=`, żeby zapytanie listy
  // ciągnęło tylko renderowane dane. „Zawsze dołącz" (niezależne od widoczności kolumn):
  // is_active/gcrecord czyta odznaka statusu w kolumnie nazwiska; has_account/work_email —
  // menu akcji wiersza i modale (usuń/anonimizuj/utwórz konto) czytają je z wiersza listy.
  // Towarzysze: nazwisko renderuje też imię i drugie imię (jedna kolumna, trzy pola).
  //
  // UWAGA UTRZYMANIA: w trybie projekcji backend zwraca wiersz CZĘŚCIOWY (typowany jako pełny
  // EmployeeListItem — kompilator tego nie wyłapie). Każde NOWE pole czytane z wiersza listy
  // POZA własną komórką widocznej kolumny (nowy modal/odznaka/guard) MUSI dostać slug w
  // alwaysSlugs/companions — inaczej w projekcji będzie ciche `undefined`.
  const visibleColumnParams = useMemo(
    () =>
      computeVisibleColumnParams({
        columns,
        savedLayoutColumns: listLayout?.layoutConfig?.columns,
        coreIdToSlug,
        extSlugSet: new Set(extColumns.map((c) => c.id)),
        alwaysSlugs: ['is_active', 'gcrecord', 'has_account', 'work_email'],
        companions: [
          { whenSlug: 'last_name', addSlug: 'first_name' },
          { whenSlug: 'last_name', addSlug: 'middle_name' },
        ],
        // Pola kalendarza układu zawsze w `?columns=` — kalendarz nie zależy od widocznych kolumn.
        calendarConfig: listLayout?.layoutConfig?.calendarConfig,
      }),
    [
      columns,
      listLayout?.layoutConfig?.columns,
      listLayout?.layoutConfig?.calendarConfig,
      coreIdToSlug,
      extColumns,
    ],
  );

  // Filtry do backendu jako pojedyncza tablica `headerFilters`:
  // (1) popup w nagłówku (mode=values/condition/range) +
  // (2) input „Szukaj…" pod headerem (konwersja na mode=condition contains) +
  // (3) aktywny szybki filtr (preset).
  // BEZ miesiąca kalendarza — to nawigacja widoku, nie filtr; eksport CSV (E16) bierze te filtry,
  // a zapytanie listy dokłada kalendarz osobno (`listHeaderFilters` niżej).
  const viewHeaderFilters = useMemo(() => {
    const merged: ColumnHeaderFilter[] = [];

    for (const [columnId, filter] of Object.entries(headerFilters)) {
      // „Konto" jest computed (`has_account`) — backend nie ma go w mapie header-filterów,
      // więc NIGDY nie wchodzi do `headerFilters` (byłby cichym no-op); routing → `?hasAccount=`
      // niżej (`hasAccountFilter`).
      if (columnId === ACCOUNT_COLUMN_ID) continue;
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug ?? columnId;
      if (fieldSlug) merged.push({ ...filter, field: fieldSlug });
    }

    for (const [columnId, val] of Object.entries(debouncedColumnFilters)) {
      if (columnId === ACCOUNT_COLUMN_ID) continue;
      if (val == null || val === '') continue;
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug;
      if (!fieldSlug) continue;
      merged.push({
        field: fieldSlug,
        mode: 'condition',
        operator: 'contains',
        conditionValue: String(val),
      });
    }

    const activeQf = quickFilters.find((q) => q.id === activeQuickFilterId);
    if (activeQf) {
      for (const cond of activeQf.filters) {
        if (cond.field === ACCOUNT_COLUMN_ID) continue;
        const col = columns.find((c) => c.id === cond.field);
        const fieldSlug = col?.headerFilter?.fieldSlug;
        if (!fieldSlug) continue;
        merged.push({
          field: fieldSlug,
          mode: 'condition',
          operator: cond.operator as ColumnHeaderFilter['operator'],
          conditionValue: cond.value == null ? '' : String(cond.value),
        });
      }
    }

    return merged;
  }, [headerFilters, debouncedColumnFilters, quickFilters, activeQuickFilterId, columns]);
  const serializedHeaderFilters =
    viewHeaderFilters.length > 0 ? JSON.stringify(viewHeaderFilters) : undefined;
  // Widok kalendarza dociąga tylko rekordy z wyświetlanego miesiąca (wspólny filtr z silnika) —
  // tylko zapytanie listy.
  const listHeaderFilters = controls.calendarHeaderFilter
    ? JSON.stringify([...viewHeaderFilters, controls.calendarHeaderFilter])
    : serializedHeaderFilters;

  // Filtr „Konto" (kolumna wirtualna, patrz `ACCOUNT_COLUMN_ID`) czytany OSOBNO z
  // `headerFilters` i routowany do `?hasAccount=` (parametr już wspierany przez
  // `useEmployeesList`) — NIE trafia do `serializedHeaderFilters` (backend by go po cichu
  // zignorował, `has_account` nie jest w mapie header-filterów silnika list).
  const accountHeaderFilter = headerFilters[ACCOUNT_COLUMN_ID];
  const hasAccountFilter = useMemo<boolean | undefined>(() => {
    if (!accountHeaderFilter || accountHeaderFilter.mode !== 'condition') return undefined;
    if (accountHeaderFilter.conditionValue === 'true') return true;
    if (accountHeaderFilter.conditionValue === 'false') return false;
    return undefined;
  }, [accountHeaderFilter]);

  // Filtry widoku listy BEZ stronicowania i projekcji — jedno źródło dla zapytania listy i eksportu
  // CSV (E16), żeby plik miał dokładnie te same filtry co ekran.
  const listFilters = useMemo<EmployeesListFilters>(
    () => ({
      companyId,
      sort: controls.queryParams.sort,
      order: controls.queryParams.order,
      groups: selectedGroups.length > 0 ? selectedGroups : undefined,
      search: debouncedSearch || undefined,
      headerFilters: serializedHeaderFilters,
      hasAccount: hasAccountFilter,
    }),
    [
      companyId,
      controls.queryParams.sort,
      controls.queryParams.order,
      selectedGroups,
      debouncedSearch,
      serializedHeaderFilters,
      hasAccountFilter,
    ],
  );
  const employeesQ = useEmployeesList({
    ...listFilters,
    headerFilters: listHeaderFilters,
    limit: controls.queryParams.limit,
    offset: controls.queryParams.offset,
    columns: visibleColumnParams.length > 0 ? visibleColumnParams.join(',') : undefined,
  });
  setRefLabels(employeesQ.data?.extRefLabels);
  // `meta.sortable`/`meta.filterable` w stan przez EFEKT, nie w renderze (to useState → wywołanie
  // w renderze pętli).
  useEffect(() => {
    setSortableSlugs(employeesQ.data?.meta?.sortable);
  }, [employeesQ.data?.meta?.sortable]);
  useEffect(() => {
    setFilterableSlugs(employeesQ.data?.meta?.filterable);
  }, [employeesQ.data?.meta?.filterable]);
  const employees = employeesQ.data?.employees ?? [];

  // Drawer
  const initialEmployeeId = searchParams.get('employeeId');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(initialEmployeeId);
  const employeeDetailQ = useEmployeeDetail(selectedEmployeeId);

  // Modale
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<EmployeeDetail | null>(null);
  const [deletingEmployee, setDeletingEmployee] = useState<EmployeeListItem | null>(null);
  const [anonymizingEmployee, setAnonymizingEmployee] = useState<EmployeeListItem | null>(null);
  const [createAccountFor, setCreateAccountFor] = useState<EmployeeListItem | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Eksport CSV (E16) — dokładnie to, co lista. „Eksport listy": te same filtry co zapytanie listy
  // (`listFilters`: grupy, filtry kolumn i szybki filtr, „Konto", sort, wybrana firma), wszystkie
  // strony. „Eksport zaznaczonych": tylko wskazane ID (+ firma i sort listy). Plik zawiera
  // WYŁĄCZNIE kolumny widoczne w tabeli, w jej kolejności i z jej nagłówkami.
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[] | null>(null);
  const extSlugSet = useMemo(() => new Set(extColumns.map((c) => c.id)), [extColumns]);
  const listExport = useEngineListExport<EmployeeListItem>({
    target: 'employees',
    filename: 'pracownicy',
    buildFilterParams: () => new URLSearchParams(buildEmployeesListParams(listFilters)),
    columns,
    visibleColumnIds,
    // Dopóki katalog pól i układ listy się ładują, eksport nie startuje (niepełna lista kolumn).
    ready: fieldsQ.isSuccess && !layoutLoading,
    columnSlug: (id) => coreIdToSlug[id] ?? (extSlugSet.has(id) ? id : undefined),
    // Komórka „Imię i nazwisko" czyta też imię i drugie imię (jak `companions` listy).
    extraSlugs: ['first_name', 'middle_name'],
    // Etykiety odwołań (firma, pola `reference`) z odpowiedzi eksportu — obejmują WSZYSTKIE
    // wyeksportowane wiersze, nie tylko bieżącą stronę listy. Te same buildery co tabela.
    buildExportColumns: (refLabels) => {
      const getLabels = () => refLabels as ExtRefLabels | undefined;
      return assembleColumns(
        buildEmployeeCoreColumns(getLabels),
        buildExtColumns<EmployeeListItem>(fieldsQ.data ?? [], t, getLabels),
        getLabels,
      );
    },
    // Komórka „Imię i nazwisko" to link + plakietki archiwum/usunięcia — do pliku sama nazwa.
    textOverrides: {
      last_name: (row) =>
        [row.lastName, row.firstName, row.middleName].filter((p) => !!p).join(' '),
    },
    layoutColumns: listLayout?.layoutConfig?.columns,
    formattingRules: listLayout?.layoutConfig?.formattingRules,
    heading: t('tenant.employees.title'),
  });

  // Akcje listy (PHASE 25) — generyczny hook. Eksporty (zaznaczonych i całej listy) składa
  // strona z widocznych kolumn (`handlers`); importu pracownicy nie mają → „wkrótce".
  // Archiwizacja/przywracanie = rozgłoszenie DELETE/POST po ID. Bogate lokalne
  // menu wiersza (utwórz konto / anonimizuj) zostaje nietknięte.
  const employeeActions = useListActions({
    kartotekaKey: 'employees',
    entity: 'employees',
    queryKey: ['employees', 'list'],
    selectedIds,
    setSelectedIds,
    listActionsConfig: listLayout?.layoutConfig?.listActions,
    customButtons: listLayout?.layoutConfig?.customActionButtons,
    handlers: { ...listExport.handlers },
    testIdPrefix: 'employees',
  });
  const [openMenuFor, setOpenMenuFor] = useState<{ id: string; top: number; right: number } | null>(
    null,
  );

  // Sync drawer state ↔ URL.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (selectedEmployeeId) next.set('employeeId', selectedEmployeeId);
    else next.delete('employeeId');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployeeId]);

  // Usunięcie, przywrócenie i edycja pracownika to zwykły zapis rekordu — na czas zadania przycisku SQL na
  // nim serwer odpowiada 409 `RECORD_LOCKED_BY_RUN`; zamiast ogólnego błędu pokazujemy, które zadanie blokuje.
  const reportRecordLock = useReportRecordLock();

  // ----- Mutacje -----
  const deleteMutation = useMutation({
    mutationFn: async (employeeId: string) => {
      const res = await api.delete(`/employees/${employeeId}`);
      return res.data;
    },
    onSuccess: (_d, employeeId) => {
      queryClient.invalidateQueries({ queryKey: ['employees', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['distinct-values', 'employees'] });
      if (selectedEmployeeId === employeeId) setSelectedEmployeeId(null);
      toast.show({ variant: 'success', title: t('tenant.employees.toast.deleted') });
      setDeletingEmployee(null);
    },
    onError: (err) => {
      if (reportRecordLock(err)) return;
      handleApiError(err, t, toast, 'tenant.employees.toast.deleteError');
    },
  });

  const editMutation = useMutation({
    mutationFn: async ({
      employeeId,
      body,
    }: {
      employeeId: string;
      body: Record<string, unknown>;
    }) => {
      const res = await api.patch(`/employees/${employeeId}`, body);
      return res.data;
    },
    onSuccess: (_d, { employeeId }) => {
      queryClient.invalidateQueries({ queryKey: ['employees', 'list'] });
      queryClient.invalidateQueries({ queryKey: EMPLOYEE_DETAIL_KEY(employeeId) });
      queryClient.invalidateQueries({ queryKey: ['distinct-values', 'employees'] });
      toast.show({ variant: 'success', title: t('tenant.employees.toast.updated') });
      setEditingEmployee(null);
    },
    onError: (err) => {
      if (reportRecordLock(err)) return;
      handleApiError(err, t, toast, 'tenant.employees.toast.updateError');
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (employeeId: string) => {
      const res = await api.post(`/employees/${employeeId}/restore`, {});
      return res.data;
    },
    onSuccess: (_d, employeeId) => {
      queryClient.invalidateQueries({ queryKey: ['employees', 'list'] });
      queryClient.invalidateQueries({ queryKey: EMPLOYEE_DETAIL_KEY(employeeId) });
      queryClient.invalidateQueries({ queryKey: ['distinct-values', 'employees'] });
      toast.show({ variant: 'success', title: t('tenant.employees.toast.restored') });
    },
    onError: (err) => {
      if (reportRecordLock(err)) return;
      handleApiError(err, t, toast, 'tenant.employees.toast.restoreError');
    },
  });

  const anonymizeMutation = useMutation({
    mutationFn: async (employeeId: string) => {
      const res = await api.post(`/employees/${employeeId}/anonymize`, {});
      return res.data;
    },
    onSuccess: (_d, employeeId) => {
      queryClient.invalidateQueries({ queryKey: ['employees', 'list'] });
      queryClient.invalidateQueries({ queryKey: EMPLOYEE_DETAIL_KEY(employeeId) });
      queryClient.invalidateQueries({ queryKey: ['distinct-values', 'employees'] });
      toast.show({ variant: 'success', title: t('tenant.employees.toast.anonymized') });
      setAnonymizingEmployee(null);
    },
    onError: (err) => {
      if (reportRecordLock(err)) return;
      handleApiError(err, t, toast, 'tenant.employees.toast.anonymizeError');
    },
  });

  // ----- Render -----
  return (
    <main className="flex h-full min-h-0 flex-col bg-bg-secondary p-6" data-testid="employees-page">
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <header className="flex shrink-0 flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-text-primary">{t('tenant.employees.title')}</h1>
            <p className="mt-1 text-sm text-text-tertiary">{t('tenant.employees.subtitle')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ListViewModeToggle {...viewModes.toggleProps} testIdPrefix="employees" />
            <ListActionsMenu {...employeeActions.menuProps} />
            <ListBuilderMenu slug="employees" testIdPrefix="employees" />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/org-chart')}
              leftIcon={<Network size={16} aria-hidden="true" />}
              data-testid="employees-org-chart-btn"
            >
              {t('tenant.orgChart.title')}
            </Button>
            {hasScheduleModule && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate('/grafik')}
                leftIcon={<CalendarDays size={16} aria-hidden="true" />}
                data-testid="employees-schedule-btn"
              >
                {t('tenant.schedule.title')}
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(withMenuItem('/employees/new'))}
              leftIcon={<Plus size={16} aria-hidden="true" />}
              data-testid="employees-add-btn"
            >
              {t('tenant.employees.addCta')}
            </Button>
          </div>
        </header>

        <div className="shrink-0">
          <ListTopWidgets tableId={tableId} categoryId={null} companyId={companyId} />
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {fieldsQ.isError ? (
            <FieldCatalogErrorState onRetry={() => void fieldsQ.refetch()} />
          ) : (
            <ConfigurableTable<EmployeeListItem>
              tableId={tableId}
              testIdPrefix="employees"
              showOrdinal
              fillHeight
              /* Górny pasek listy WEWNĄTRZ karty tabeli (parytet z Figmą): chipy grup po lewej,
                 baton odśwież/filtry po prawej; presety w osobnym rzędzie pod spodem. */
              listToolbar={
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Chipy filtra = GRUPY ze słownika (wielokrotny wybór, OR). */}
                    <GroupChipBar
                      dictionaryCode="employee_groups"
                      selected={selectedGroups}
                      onChange={(next) => {
                        setSelectedGroups(next);
                        controls.resetPage();
                      }}
                      className="flex-1"
                      data-testid="employees-group-chip-bar"
                    />
                    <div className="ml-auto flex shrink-0 items-center gap-2">
                      <ListFilterBar
                        tableId={tableId}
                        columns={columns}
                        search={search}
                        onSearchChange={(next) => {
                          setSearch(next);
                          controls.resetPage();
                        }}
                        searchPlaceholder={t('tenant.employees.searchPlaceholder')}
                        showSearch={false}
                        headerFilters={headerFilters}
                        onHeaderFiltersChange={handleHeaderFiltersChange}
                        onRefresh={() =>
                          queryClient.invalidateQueries({ queryKey: ['employees', 'list'] })
                        }
                        testIdPrefix="employees"
                      />
                    </div>
                  </div>
                  <QuickFilterBar
                    quickFilters={quickFilters}
                    activeId={activeQuickFilterId}
                    onChange={(id) => {
                      setActiveQuickFilterId(id);
                      controls.resetPage();
                    }}
                  />
                </div>
              }
              {...viewModes.tableProps}
              columns={columns}
              data={employees}
              isLoading={employeesQ.isLoading}
              getRowId={(row) => row.id}
              selectable
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              onVisibleColumnsChange={setVisibleColumnIds}
              onRowContextMenu={employeeActions.onRowContextMenu}
              headerFilters={headerFilters}
              onHeaderFiltersChange={handleHeaderFiltersChange}
              filterable
              columnFilters={columnFilters}
              onColumnFiltersChange={(next) => {
                setColumnFilters(next);
                controls.resetPage();
              }}
              {...controls.tableProps}
              totalItems={employeesQ.data?.total ?? 0}
              isFetching={employeesQ.isFetching}
              onCalendarRangeChange={controls.setCalendarRange}
              onRowClick={(row) => navigate(withMenuItem(`/employees/${row.id}`))}
              renderActions={(row) => (
                <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="rounded p-1.5 text-text-muted hover:bg-bg-secondary"
                    onClick={(e) => {
                      if (openMenuFor?.id === row.id) {
                        setOpenMenuFor(null);
                        return;
                      }
                      const rect = e.currentTarget.getBoundingClientRect();
                      const dropdownH = 160;
                      const spaceBelow = window.innerHeight - rect.bottom;
                      const top = spaceBelow < dropdownH ? rect.top - dropdownH : rect.bottom + 4;
                      setOpenMenuFor({
                        id: row.id,
                        top,
                        right: window.innerWidth - rect.right,
                      });
                    }}
                    aria-label={t('tenant.employees.actions.menu')}
                    data-testid={`employees-row-menu-${row.id}`}
                  >
                    <MoreHorizontal size={16} aria-hidden="true" />
                  </button>
                  {openMenuFor?.id === row.id && (
                    <RowActionsMenu
                      employee={row}
                      customItems={employeeActions.rowButtonItems(row.id)}
                      menuPos={{ top: openMenuFor.top, right: openMenuFor.right }}
                      onClose={() => setOpenMenuFor(null)}
                      onCreateAccount={() => {
                        setCreateAccountFor(row);
                        setOpenMenuFor(null);
                      }}
                      onAnonymize={() => {
                        setAnonymizingEmployee(row);
                        setOpenMenuFor(null);
                      }}
                      onDelete={() => {
                        setDeletingEmployee(row);
                        setOpenMenuFor(null);
                      }}
                      onRestore={() => {
                        restoreMutation.mutate(row.id);
                        setOpenMenuFor(null);
                      }}
                    />
                  )}
                </div>
              )}
              actionsColumnLabel={t('tenant.employees.col.actions')}
              emptyIcon={Users}
              emptyTitle={t('tenant.employees.empty')}
            />
          )}
        </div>
      </div>

      {/* Drawer szczegółów */}
      {selectedEmployeeId && (
        <EmployeeDetailDrawer
          employeeId={selectedEmployeeId}
          detailQ={employeeDetailQ}
          onClose={() => setSelectedEmployeeId(null)}
          onEdit={(emp) => setEditingEmployee(emp)}
          onCreateAccount={(emp) =>
            setCreateAccountFor({
              id: emp.id,
              firstName: emp.firstName,
              lastName: emp.lastName,
              middleName: emp.middleName,
              birthDate: emp.birthDate,
              taxId: emp.taxId,
              idDocumentType: emp.idDocumentType,
              pesel: null,
              idDocumentNumber: null,
              companyId: emp.companyId,
              orgUnitId: emp.orgUnitId,
              positionCode: emp.positionCode,
              specialization: emp.specialization,
              employmentType: emp.employmentType,
              employmentStartDate: emp.employmentStartDate,
              employmentEndDate: emp.employmentEndDate,
              internalCode: emp.internalCode,
              managerEmployeeId: emp.managerEmployeeId,
              phone: emp.phone,
              mobile: emp.mobile,
              workEmail: emp.workEmail,
              isActive: emp.isActive,
              hasAccount: !!emp.account,
              createdAt: emp.createdAt,
              gcrecord: emp.gcrecord,
            })
          }
          onAnonymize={(emp) => setAnonymizingEmployee(detailToListItem(emp, !!emp.account))}
          onDelete={(emp) => setDeletingEmployee(detailToListItem(emp, !!emp.account))}
          onRestore={(emp) => restoreMutation.mutate(emp.id)}
        />
      )}

      {/* Modal Edit */}
      {editingEmployee && (
        <EditEmployeeModal
          employee={editingEmployee}
          companies={companies}
          loading={editMutation.isPending}
          onClose={() => setEditingEmployee(null)}
          onSave={(body) => editMutation.mutate({ employeeId: editingEmployee.id, body })}
        />
      )}

      {/* Modal Create */}
      {createModalOpen && (
        <CreateEmployeeModal
          onClose={() => setCreateModalOpen(false)}
          companies={companies}
          onCreated={() => {
            setCreateModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ['employees', 'list'] });
          }}
        />
      )}

      {/* Modal Anonymize */}
      {anonymizingEmployee && (
        <AnonymizeEmployeeModal
          employee={anonymizingEmployee}
          loading={anonymizeMutation.isPending}
          onClose={() => setAnonymizingEmployee(null)}
          onConfirm={() => anonymizeMutation.mutate(anonymizingEmployee.id)}
        />
      )}

      {/* Modal Delete */}
      {deletingEmployee && (
        <DeleteEmployeeModal
          employee={deletingEmployee}
          loading={deleteMutation.isPending}
          onClose={() => setDeletingEmployee(null)}
          onConfirm={() => deleteMutation.mutate(deletingEmployee.id)}
        />
      )}

      {/* Modal CreateAccount (dla istniejącego employee bez konta) */}
      {createAccountFor && (
        <CreateAccountForExistingModal
          employeeId={createAccountFor.id}
          firstName={createAccountFor.firstName}
          lastName={createAccountFor.lastName}
          companyId={createAccountFor.companyId}
          defaultEmail={createAccountFor.workEmail}
          onClose={() => setCreateAccountFor(null)}
          onCreated={() => {
            setCreateAccountFor(null);
            queryClient.invalidateQueries({ queryKey: ['employees', 'list'] });
            if (selectedEmployeeId) {
              queryClient.invalidateQueries({
                queryKey: EMPLOYEE_DETAIL_KEY(selectedEmployeeId),
              });
            }
            toast.show({
              variant: 'success',
              title: t('tenant.employees.toast.accountCreated'),
            });
          }}
        />
      )}

      {employeeActions.overlays}
    </main>
  );
}

// ============================================================================
// RowActionsMenu
// ============================================================================

function RowActionsMenu({
  employee,
  customItems,
  menuPos,
  onClose,
  onCreateAccount,
  onAnonymize,
  onDelete,
  onRestore,
}: {
  employee: EmployeeListItem;
  /** Własne przyciski „Akcja wiersza" z Kreatora Listy. */
  customItems: RowActionItem[];
  menuPos: { top: number; right: number };
  onClose: () => void;
  onCreateAccount: () => void;
  onAnonymize: () => void;
  onDelete: () => void;
  onRestore: () => void;
}) {
  const { t } = useTranslation();
  const isDeleted = employee.gcrecord !== null;
  return (
    <>
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        aria-hidden="true"
        data-testid="row-menu-backdrop"
      />
      <div
        style={{ top: menuPos.top, right: menuPos.right }}
        className="fixed z-50 w-56 rounded-lg border border-border-primary bg-bg-primary py-1 shadow-lg"
        role="menu"
        data-testid={`employees-row-menu-popup-${employee.id}`}
      >
        {!isDeleted && <CustomRowMenuItems items={customItems} onAfterSelect={onClose} />}
        {!isDeleted && !employee.hasAccount && (
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-bg-tertiary"
            onClick={onCreateAccount}
            data-testid={`employees-action-create-account-${employee.id}`}
          >
            <UserPlus size={14} aria-hidden="true" />
            {t('tenant.employees.actions.createAccount')}
          </button>
        )}
        {!isDeleted && (
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-amber-700 hover:bg-bg-tertiary"
            onClick={onAnonymize}
            data-testid={`employees-action-anonymize-${employee.id}`}
          >
            <AlertTriangle size={14} aria-hidden="true" />
            {t('tenant.employees.actions.anonymize')}
          </button>
        )}
        {!isDeleted ? (
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-700 hover:bg-bg-tertiary"
            onClick={onDelete}
            data-testid={`employees-action-delete-${employee.id}`}
          >
            <Trash2 size={14} aria-hidden="true" />
            {t('tenant.employees.actions.delete')}
          </button>
        ) : (
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-bg-tertiary"
            onClick={onRestore}
            data-testid={`employees-action-restore-${employee.id}`}
          >
            <RotateCcw size={14} aria-hidden="true" />
            {t('tenant.employees.actions.restore')}
          </button>
        )}
      </div>
    </>
  );
}

// ============================================================================
// EmployeeDetailDrawer (3 zakładki + akcje)
// ============================================================================

function EmployeeDetailDrawer({
  employeeId,
  detailQ,
  onClose,
  onEdit,
  onCreateAccount,
  onAnonymize,
  onDelete,
  onRestore,
}: {
  employeeId: string;
  detailQ: ReturnType<typeof useEmployeeDetail>;
  onClose: () => void;
  onEdit: (emp: EmployeeDetail) => void;
  onCreateAccount: (emp: EmployeeDetail) => void;
  onAnonymize: (emp: EmployeeDetail) => void;
  onDelete: (emp: EmployeeDetail) => void;
  onRestore: (emp: EmployeeDetail) => void;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<DrawerTab>('personal');
  const emp = detailQ.data?.employee;

  const orgUnitsQ = useOrgUnitsForCompany(emp?.companyId ?? null);
  const orgUnit = emp?.orgUnitId
    ? orgUnitsQ.data?.orgUnits.find((u) => u.id === emp.orgUnitId)
    : null;
  const positionsDict = useDictionary('employee_positions');
  const positionLabel = emp?.positionCode
    ? positionsDict.itemsByCode.get(emp.positionCode)
      ? formatDictionaryOptionLabel(
          positionsDict.itemsByCode.get(emp.positionCode)!,
          positionsDict.dictionary?.displayMode ?? 'label',
        )
      : emp.positionCode
    : null;

  const companiesQ = useCompanies();
  const companyName = emp?.companyId
    ? (companiesQ.data?.companies.find((c) => c.id === emp.companyId)?.shortName ??
      companiesQ.data?.companies.find((c) => c.id === emp.companyId)?.legalName ??
      null)
    : null;

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        emp ? (
          <span>
            {emp.lastName} {emp.firstName}
            {emp.middleName ? ` ${emp.middleName}` : ''}
          </span>
        ) : (
          t('tenant.employees.drawer.title')
        )
      }
    >
      {detailQ.isLoading ? (
        <div className="flex items-center justify-center py-10" role="status">
          <Spinner />
        </div>
      ) : !emp ? (
        <div role="alert" className="px-3 py-6 text-sm text-red-700">
          {t('tenant.employees.drawer.error')}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Toolbar akcji */}
          <DrawerActionsToolbar
            employee={emp}
            onEdit={() => onEdit(emp)}
            onCreateAccount={() => onCreateAccount(emp)}
            onAnonymize={() => onAnonymize(emp)}
            onDelete={() => onDelete(emp)}
            onRestore={() => onRestore(emp)}
          />

          {/* Tabs */}
          <div role="tablist" className="flex gap-2 border-b border-border-primary">
            <DrawerTabButton
              active={tab === 'personal'}
              onClick={() => setTab('personal')}
              testId="employee-tab-personal"
            >
              {t('tenant.employees.drawer.tabs.personal')}
            </DrawerTabButton>
            <DrawerTabButton
              active={tab === 'employment'}
              onClick={() => setTab('employment')}
              testId="employee-tab-employment"
            >
              {t('tenant.employees.drawer.tabs.employment')}
            </DrawerTabButton>
            <DrawerTabButton
              active={tab === 'addresses'}
              onClick={() => setTab('addresses')}
              testId="employee-tab-addresses"
            >
              {t('tenant.employees.drawer.tabs.addresses')}
            </DrawerTabButton>
          </div>

          {tab === 'personal' && <PersonalTab employee={emp} />}
          {tab === 'employment' && (
            <EmploymentTab
              employee={emp}
              orgUnitName={orgUnit?.name ?? null}
              positionLabel={positionLabel}
              companyName={companyName}
            />
          )}
          {tab === 'addresses' && <AddressesTab employeeId={employeeId} />}
        </div>
      )}
    </Drawer>
  );
}

function DrawerTabButton({
  active,
  onClick,
  testId,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testId?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testId}
      className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
        active
          ? 'border-accent-600 text-accent-700 dark:text-accent-400'
          : 'border-transparent text-text-tertiary hover:text-text-primary'
      }`}
    >
      {children}
    </button>
  );
}

function DrawerActionsToolbar({
  employee,
  onEdit,
  onCreateAccount,
  onAnonymize,
  onDelete,
  onRestore,
}: {
  employee: EmployeeDetail;
  onEdit: () => void;
  onCreateAccount: () => void;
  onAnonymize: () => void;
  onDelete: () => void;
  onRestore: () => void;
}) {
  const { t } = useTranslation();
  const isDeleted = employee.gcrecord !== null;
  return (
    <div className="flex flex-wrap gap-2">
      {!isDeleted && (
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Pencil size={14} aria-hidden="true" />}
          onClick={onEdit}
          data-testid="employee-drawer-edit"
        >
          {t('tenant.employees.actions.edit')}
        </Button>
      )}
      {!isDeleted && !employee.account && (
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<UserPlus size={14} aria-hidden="true" />}
          onClick={onCreateAccount}
          data-testid="employee-drawer-create-account"
        >
          {t('tenant.employees.actions.createAccount')}
        </Button>
      )}
      {!isDeleted && (
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<AlertTriangle size={14} aria-hidden="true" />}
          onClick={onAnonymize}
          data-testid="employee-drawer-anonymize"
        >
          {t('tenant.employees.actions.anonymize')}
        </Button>
      )}
      {!isDeleted ? (
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Trash2 size={14} aria-hidden="true" />}
          onClick={onDelete}
          data-testid="employee-drawer-delete"
        >
          {t('tenant.employees.actions.delete')}
        </Button>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<RotateCcw size={14} aria-hidden="true" />}
          onClick={onRestore}
          data-testid="employee-drawer-restore"
        >
          {t('tenant.employees.actions.restore')}
        </Button>
      )}
    </div>
  );
}

// ============================================================================
// PersonalTab — dane osobowe + dane wrażliwe inline (PESEL, dowód)
// ============================================================================

function PersonalTab({ employee }: { employee: EmployeeDetail }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3 text-sm">
      <DrawerField label={t('tenant.employees.field.firstName')} value={employee.firstName} />
      <DrawerField label={t('tenant.employees.field.lastName')} value={employee.lastName} />
      <DrawerField
        label={t('tenant.employees.field.middleName')}
        value={employee.middleName ?? '—'}
      />
      <DrawerField
        label={t('tenant.employees.field.birthDate')}
        value={formatDate(employee.birthDate)}
      />

      {/* Sekcja: dane wrażliwe */}
      <div className="mt-4 rounded-xl border border-[#fbbdb8] bg-[#fef1f0] p-3 dark:border-red-900 dark:bg-red-950/30">
        <div className="mb-2 flex items-center gap-2 text-xs font-medium text-[#b42318] dark:text-red-300">
          <Lock size={12} aria-hidden="true" />
          {t('tenant.employees.sensitive.heading')}
        </div>
        <SensitiveField
          label={t('tenant.employees.field.pesel')}
          value={employee.pesel}
          masked={employee.peselMasked}
          testId="employee-field-pesel"
        />
        <SensitiveField
          label={t('tenant.employees.field.idDocumentNumber')}
          value={employee.idDocumentNumber}
          masked={employee.idDocumentMasked}
          testId="employee-field-id-document"
        />
        {employee.peselMasked && (
          <p className="mt-2 text-xs text-[#b42318] dark:text-red-300">
            {t('tenant.employees.sensitive.maskedHint')}
          </p>
        )}
      </div>

      <DrawerField label={t('tenant.employees.field.taxId')} value={employee.taxId ?? '—'} />
      <DrawerField
        label={t('tenant.employees.field.idDocumentType')}
        value={
          employee.idDocumentType
            ? t(`tenant.employees.idDocumentType.${employee.idDocumentType}`)
            : '—'
        }
      />
    </div>
  );
}

function SensitiveField({
  label,
  value,
  masked,
  testId,
}: {
  label: string;
  value: string | null;
  masked: boolean;
  testId?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex justify-between py-1 text-sm" data-testid={testId}>
      <span className="text-text-tertiary">{label}</span>
      <span
        className={
          value === null
            ? 'text-text-tertiary'
            : masked
              ? 'font-mono text-text-tertiary'
              : 'font-mono text-text-primary'
        }
      >
        {value ?? t('tenant.employees.sensitive.notSet')}
      </span>
    </div>
  );
}

function DrawerField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-1">
      <span className="text-text-tertiary">{label}</span>
      <span className="text-text-primary">{value}</span>
    </div>
  );
}

// ============================================================================
// EmploymentTab — dane zatrudnienia
// ============================================================================

function EmploymentTab({
  employee,
  orgUnitName,
  positionLabel,
  companyName,
}: {
  employee: EmployeeDetail;
  orgUnitName: string | null;
  positionLabel: string | null;
  companyName: string | null;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3 text-sm">
      <DrawerField label={t('tenant.employees.field.company')} value={companyName ?? '—'} />
      <DrawerField label={t('tenant.employees.field.position')} value={positionLabel ?? '—'} />
      <DrawerField
        label={t('tenant.employees.field.specialization')}
        value={employee.specialization ?? '—'}
      />
      <DrawerField label={t('tenant.employees.field.orgUnit')} value={orgUnitName ?? '—'} />
      <DrawerField
        label={t('tenant.employees.field.employmentType')}
        value={
          employee.employmentType
            ? t(`tenant.employees.employmentType.${employee.employmentType}`)
            : '—'
        }
      />
      <DrawerField
        label={t('tenant.employees.field.employmentStartDate')}
        value={formatDate(employee.employmentStartDate)}
      />
      <DrawerField
        label={t('tenant.employees.field.employmentEndDate')}
        value={formatDate(employee.employmentEndDate)}
      />
      <DrawerField
        label={t('tenant.employees.field.internalCode')}
        value={employee.internalCode ?? '—'}
      />
      <DrawerField label={t('tenant.employees.field.phone')} value={employee.phone ?? '—'} />
      <DrawerField label={t('tenant.employees.field.mobile')} value={employee.mobile ?? '—'} />
      <DrawerField
        label={t('tenant.employees.field.workEmail')}
        value={employee.workEmail ?? '—'}
      />
      <DrawerField
        label={t('tenant.employees.field.isActive')}
        value={
          employee.isActive ? t('tenant.employees.isActive.yes') : t('tenant.employees.isActive.no')
        }
      />
      {employee.account && (
        <div className="mt-3 rounded-lg border border-border-primary bg-bg-tertiary p-3">
          <div className="mb-1 flex items-center gap-2 text-xs font-medium text-text-secondary">
            <KeyRound size={12} aria-hidden="true" />
            {t('tenant.employees.account.heading')}
          </div>
          <div className="text-sm">{employee.account.email}</div>
          <div className="mt-1">
            <Badge color={employee.account.status === 'active' ? 'green' : 'gray'}>
              {t(`tenant.users.status.${employee.account.status}`)}
            </Badge>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// AddressesTab — pełen CRUD adresów dla owner_type='employee'
// ============================================================================

interface AddressFormState {
  kind: AddressKind;
  label: string;
  street: string;
  buildingNo: string;
  apartmentNo: string;
  postalCode: string;
  city: string;
  region: string;
  countryCode: string;
  isPrimary: boolean;
  isActive: boolean;
  notes: string;
}

const EMPTY_ADDRESS_FORM: AddressFormState = {
  kind: 'registered',
  label: '',
  street: '',
  buildingNo: '',
  apartmentNo: '',
  postalCode: '',
  city: '',
  region: '',
  countryCode: 'PL',
  isPrimary: false,
  isActive: true,
  notes: '',
};

function addressRowToForm(row: AddressRow): AddressFormState {
  return {
    kind: (row.kind as AddressKind) ?? 'registered',
    label: row.label ?? '',
    street: row.street ?? '',
    buildingNo: row.buildingNo ?? '',
    apartmentNo: row.apartmentNo ?? '',
    postalCode: row.postalCode ?? '',
    city: row.city ?? '',
    region: row.region ?? '',
    countryCode: row.countryCode ?? 'PL',
    isPrimary: row.isPrimary,
    isActive: row.isActive,
    notes: row.notes ?? '',
  };
}

function AddressesTab({ employeeId }: { employeeId: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<AddressRow | null>(null);
  const [form, setForm] = useState<AddressFormState>(EMPTY_ADDRESS_FORM);
  const [pendingDelete, setPendingDelete] = useState<AddressRow | null>(null);

  const addressesQ = useQuery({
    queryKey: ['addresses', 'employee', employeeId],
    queryFn: async () => {
      const res = await api.get<{ addresses: AddressRow[] }>('/addresses', {
        params: { ownerType: 'employee', ownerId: employeeId },
      });
      return res.data.addresses;
    },
  });

  const countriesQ = useQuery({
    queryKey: ['countries'],
    queryFn: async () => {
      const res = await api.get<{ countries: CountryRow[] }>('/countries');
      return res.data.countries;
    },
    staleTime: 10 * 60 * 1000,
  });

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_ADDRESS_FORM);
    setShowModal(true);
  }

  function openEdit(addr: AddressRow) {
    setEditing(addr);
    setForm(addressRowToForm(addr));
    setShowModal(true);
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        ownerType: 'employee',
        ownerId: employeeId,
        kind: form.kind,
        label: form.label || null,
        street: form.street || null,
        buildingNo: form.buildingNo || null,
        apartmentNo: form.apartmentNo || null,
        postalCode: form.postalCode || null,
        city: form.city || null,
        region: form.region || null,
        countryCode: form.countryCode || null,
        isPrimary: form.isPrimary,
        isActive: form.isActive,
        notes: form.notes || null,
      };
      if (editing) {
        const { ownerType: _ot, ownerId: _oi, ...patchPayload } = payload;
        const res = await api.patch(`/addresses/${editing.id}`, patchPayload);
        return res.data;
      }
      const res = await api.post('/addresses', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addresses', 'employee', employeeId] });
      toast.show({
        variant: 'success',
        title: t(
          editing
            ? 'tenant.employees.addresses.toast.updated'
            : 'tenant.employees.addresses.toast.created',
        ),
      });
      setShowModal(false);
    },
    onError: (err) => handleApiError(err, t, toast, 'tenant.employees.addresses.toast.saveError'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/addresses/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addresses', 'employee', employeeId] });
      toast.show({
        variant: 'success',
        title: t('tenant.employees.addresses.toast.deleted'),
      });
    },
    onError: (err) => handleApiError(err, t, toast, 'tenant.employees.addresses.toast.deleteError'),
  });

  const addresses = addressesQ.data ?? [];
  const countries = countriesQ.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <MapPin size={14} aria-hidden="true" />
          {t('tenant.employees.addresses.heading')}
        </div>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Plus size={14} aria-hidden="true" />}
          onClick={openCreate}
          data-testid="employee-addresses-add"
        >
          {t('tenant.employees.addresses.add')}
        </Button>
      </div>
      {addressesQ.isLoading ? (
        <div className="py-4 text-center" role="status">
          <Spinner />
        </div>
      ) : addresses.length === 0 ? (
        <div className="rounded-lg border border-border-primary bg-bg-tertiary px-3 py-4 text-center text-sm text-text-tertiary">
          {t('tenant.employees.addresses.empty')}
        </div>
      ) : (
        <div className="space-y-2">
          {addresses.map((addr) => (
            <div
              key={addr.id}
              className="rounded-lg border border-border-primary bg-bg-primary p-3"
              data-testid={`employee-address-row-${addr.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge color="blue">
                      {t(`tenant.employees.addresses.kind.${addr.kind}`, {
                        defaultValue: addr.kind,
                      })}
                    </Badge>
                    {addr.isPrimary && (
                      <Badge color="green">{t('tenant.employees.addresses.primary')}</Badge>
                    )}
                    {!addr.isActive && (
                      <Badge color="gray">{t('tenant.employees.addresses.inactive')}</Badge>
                    )}
                  </div>
                  {addr.label && (
                    <div className="mt-1 text-xs text-text-tertiary">{addr.label}</div>
                  )}
                  <div className="mt-1 text-text-primary">
                    {[addr.street, addr.buildingNo, addr.apartmentNo && `/${addr.apartmentNo}`]
                      .filter(Boolean)
                      .join(' ')}
                  </div>
                  <div className="text-text-secondary">
                    {[addr.postalCode, addr.city].filter(Boolean).join(' ')}
                    {addr.region ? `, ${addr.region}` : ''}
                    {addr.countryCode ? `, ${addr.countryCode}` : ''}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => openEdit(addr)}
                    className="rounded p-1 text-xs hover:bg-bg-tertiary"
                    data-testid={`employee-address-edit-${addr.id}`}
                  >
                    {t('tenant.employees.addresses.edit')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingDelete(addr)}
                    className="rounded p-1 text-xs text-red-700 hover:bg-bg-tertiary"
                    data-testid={`employee-address-delete-${addr.id}`}
                  >
                    {t('tenant.employees.addresses.delete')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        variant="danger"
        title={t('tenant.employees.addresses.delete')}
        message={t('tenant.employees.addresses.confirmDelete')}
        confirmLabel={t('tenant.employees.addresses.delete')}
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
          setPendingDelete(null);
        }}
        onClose={() => setPendingDelete(null)}
      />

      {showModal && (
        <Modal
          open
          onClose={() => setShowModal(false)}
          title={
            editing
              ? t('tenant.employees.addresses.modal.editTitle')
              : t('tenant.employees.addresses.modal.createTitle')
          }
          size="lg"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
                data-testid="employee-address-save"
              >
                {t('common.save')}
              </Button>
            </div>
          }
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label={t('tenant.employees.addresses.field.kind')}>
              <Dropdown
                value={form.kind}
                onChange={(v) => setForm((s) => ({ ...s, kind: v as AddressKind }))}
                options={ADDRESS_KINDS.map((k) => ({
                  value: k,
                  label: t(`tenant.employees.addresses.kind.${k}`),
                }))}
              />
            </FormField>
            <Input
              label={t('tenant.employees.addresses.field.label')}
              value={form.label}
              onChange={(e) => setForm((s) => ({ ...s, label: e.target.value }))}
            />
            <Input
              label={t('tenant.employees.addresses.field.street')}
              value={form.street}
              onChange={(e) => setForm((s) => ({ ...s, street: e.target.value }))}
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                label={t('tenant.employees.addresses.field.buildingNo')}
                value={form.buildingNo}
                onChange={(e) => setForm((s) => ({ ...s, buildingNo: e.target.value }))}
              />
              <Input
                label={t('tenant.employees.addresses.field.apartmentNo')}
                value={form.apartmentNo}
                onChange={(e) => setForm((s) => ({ ...s, apartmentNo: e.target.value }))}
              />
            </div>
            <Input
              label={t('tenant.employees.addresses.field.postalCode')}
              value={form.postalCode}
              onChange={(e) => setForm((s) => ({ ...s, postalCode: e.target.value }))}
            />
            <Input
              label={t('tenant.employees.addresses.field.city')}
              value={form.city}
              onChange={(e) => setForm((s) => ({ ...s, city: e.target.value }))}
            />
            <Input
              label={t('tenant.employees.addresses.field.region')}
              value={form.region}
              onChange={(e) => setForm((s) => ({ ...s, region: e.target.value }))}
            />
            <FormField label={t('tenant.employees.addresses.field.country')}>
              <Dropdown
                value={form.countryCode}
                onChange={(v) => setForm((s) => ({ ...s, countryCode: v }))}
                options={countries.map((c) => ({
                  value: c.code,
                  label: `${c.namePl} (${c.code})`,
                }))}
              />
            </FormField>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isPrimary}
                onChange={(e) => setForm((s) => ({ ...s, isPrimary: e.target.checked }))}
              />
              {t('tenant.employees.addresses.field.isPrimary')}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((s) => ({ ...s, isActive: e.target.checked }))}
              />
              {t('tenant.employees.addresses.field.isActive')}
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}

/**
 * Select kodu pozycji ze słownika `employee_positions` (F24 Pakiet 0, dawniej wolny tekst
 * `position`) — reużywany przez oba legacy modale (Create/Edit) tej strony. Katalog ITE
 * startuje PUSTY (tenant dopisuje pozycje w Ustawienia → Słowniki), stąd podpowiedź gdy
 * `activeItems.length === 0` zamiast gołego pustego dropdownu.
 */
function PositionCodeDropdown({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  const { dictionary, activeItems } = useDictionary('employee_positions');
  return (
    <FormField label={t('tenant.employees.field.position')}>
      <Dropdown
        value={value}
        onChange={onChange}
        options={[
          { value: '', label: '—' },
          ...activeItems.map((item) => ({
            value: item.code,
            label: dictionary
              ? formatDictionaryOptionLabel(item, dictionary.displayMode)
              : item.code,
          })),
        ]}
      />
      {dictionary && activeItems.length === 0 && (
        <p className="mt-1 text-xs text-text-tertiary">
          {t('tenant.employees.field.positionCodeEmptyHint')}
        </p>
      )}
    </FormField>
  );
}

// ============================================================================
// CreateEmployeeModal — 3 zakładki, atomowy POST
// ============================================================================

interface CreateEmployeeForm {
  // Tab 1: Dane osobowe
  firstName: string;
  lastName: string;
  middleName: string;
  birthDate: string;
  pesel: string;
  taxId: string;
  idDocumentType: EmployeeIdDocumentType | '';
  idDocumentNumber: string;
  // Tab 2: Zatrudnienie
  companyId: string;
  orgUnitId: string;
  positionCode: string;
  specialization: string;
  employmentType: EmploymentType | '';
  employmentStartDate: string;
  employmentEndDate: string;
  internalCode: string;
  phone: string;
  mobile: string;
  workEmail: string;
  notes: string;
  // Tab 3: Konto
  createUser: boolean;
  userEmail: string;
  userDisplayName: string;
  userLanguage: RecordLanguage;
  userRoleTemplateId: string;
}

const EMPTY_CREATE_FORM: CreateEmployeeForm = {
  firstName: '',
  lastName: '',
  middleName: '',
  birthDate: '',
  pesel: '',
  taxId: '',
  idDocumentType: '',
  idDocumentNumber: '',
  companyId: '',
  orgUnitId: '',
  positionCode: '',
  specialization: '',
  employmentType: '',
  employmentStartDate: '',
  employmentEndDate: '',
  internalCode: '',
  phone: '',
  mobile: '',
  workEmail: '',
  notes: '',
  createUser: false,
  userEmail: '',
  userDisplayName: '',
  userLanguage: 'pl',
  userRoleTemplateId: '',
};

function CreateEmployeeModal({
  onClose,
  companies,
  onCreated,
}: {
  onClose: () => void;
  companies: Array<{ id: string; legalName: string; shortName: string | null }>;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();

  const [tab, setTab] = useState<CreateModalTab>('personal');
  // Draft persistuje w sessionStorage — zamkniecie modala i ponowne otwarcie
  // przywraca wpisane dane. clearForm() na success: czysci draft.
  const [form, setForm, clearForm] = useFormDraft<CreateEmployeeForm>(
    'employee-create',
    EMPTY_CREATE_FORM,
  );

  // Auto-pre-fill companyId z aktywnej firmy jezeli draft jeszcze nie ma firmy.
  // Bez tego user musial przelaczac na zakladke "Zatrudnienie" — UX-fail dla 99%
  // sciezek (pracownik dodaje sie do biezacej firmy). Tryb "Wszystkie firmy" (Etap 5,
  // `useActiveCompanyScope`) — NIE prefillujemy sentinela, pole zostaje puste (user
  // wybiera recznie; Etap 5 doda wymog required w tym trybie).
  // Walidacja: wybrana firma musi byc w `companies` (dostepnych dla usera per UCA).
  const companyScope = useActiveCompanyScope();
  useEffect(() => {
    if (form.companyId) return;
    if (companies.length === 0) return;
    if (companyScope.mode !== 'company' || !companyScope.companyId) return;
    const resolvedId = companyScope.companyId;
    if (companies.some((c) => c.id === resolvedId)) {
      setForm((f) => (f.companyId ? f : { ...f, companyId: resolvedId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies.length, companyScope.mode, companyScope.companyId]);

  const orgUnitsQ = useOrgUnitsForCompany(form.companyId || null);
  const roleTemplatesQ = useRoleTemplates();

  const submitMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        firstName: form.firstName,
        lastName: form.lastName,
        companyId: form.companyId,
      };
      if (form.middleName) body.middleName = form.middleName;
      if (form.birthDate) body.birthDate = form.birthDate;
      if (form.pesel) body.pesel = form.pesel;
      if (form.taxId) body.taxId = form.taxId;
      if (form.idDocumentType) body.idDocumentType = form.idDocumentType;
      if (form.idDocumentNumber) body.idDocumentNumber = form.idDocumentNumber;
      if (form.orgUnitId) body.orgUnitId = form.orgUnitId;
      if (form.positionCode) body.positionCode = form.positionCode;
      if (form.specialization) body.specialization = form.specialization;
      if (form.employmentType) body.employmentType = form.employmentType;
      if (form.employmentStartDate) body.employmentStartDate = form.employmentStartDate;
      if (form.employmentEndDate) body.employmentEndDate = form.employmentEndDate;
      if (form.internalCode) body.internalCode = form.internalCode;
      if (form.phone) body.phone = form.phone;
      if (form.mobile) body.mobile = form.mobile;
      if (form.workEmail) body.workEmail = form.workEmail;
      if (form.notes) body.notes = form.notes;
      if (form.createUser) {
        const cu: Record<string, unknown> = { language: form.userLanguage };
        if (form.userEmail) cu.email = form.userEmail;
        if (form.userDisplayName) cu.displayName = form.userDisplayName;
        if (form.userRoleTemplateId) cu.roleTemplateId = form.userRoleTemplateId;
        body.createUser = cu;
      }
      const res = await api.post('/employees', body);
      return res.data;
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: form.createUser
          ? t('tenant.employees.toast.createdWithAccount')
          : t('tenant.employees.toast.created'),
      });
      clearForm();
      onCreated();
    },
    onError: (err) => handleApiError(err, t, toast, 'tenant.employees.toast.createError'),
  });

  // Walidacja: tab 1 firstName + lastName, tab 2 companyId, tab 3 (jeśli createUser
  // i roleTemplateId) → companyId wymagane (mamy z tab 2 więc OK).
  const canSubmit =
    !!form.firstName &&
    !!form.lastName &&
    !!form.companyId &&
    (!form.createUser || form.userEmail || form.workEmail);

  return (
    <Modal
      open
      onClose={onClose}
      title={t('tenant.employees.createModal.title')}
      size="xl"
      footer={
        <div className="flex justify-between gap-2">
          <div className="text-xs text-text-tertiary self-center">
            {!canSubmit && (
              <span>
                {!form.firstName || !form.lastName
                  ? t('tenant.employees.createModal.hintRequiredPersonal')
                  : !form.companyId
                    ? t('tenant.employees.createModal.hintRequiredCompany')
                    : form.createUser && !form.userEmail && !form.workEmail
                      ? t('tenant.employees.createModal.hintRequiredEmail')
                      : ''}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => submitMutation.mutate()}
              disabled={!canSubmit || submitMutation.isPending}
              data-testid="employee-create-submit"
            >
              {t('common.create')}
            </Button>
          </div>
        </div>
      }
    >
      {/* Tabs — zawsze 3 widoczne (plan §1530 [opcjonalnie] = checkbox na tab 3) */}
      <div role="tablist" className="mb-4 flex gap-2 border-b border-border-primary">
        <DrawerTabButton
          active={tab === 'personal'}
          onClick={() => setTab('personal')}
          testId="create-tab-personal"
        >
          {t('tenant.employees.createModal.tabs.personal')}
        </DrawerTabButton>
        <DrawerTabButton
          active={tab === 'employment'}
          onClick={() => setTab('employment')}
          testId="create-tab-employment"
        >
          {t('tenant.employees.createModal.tabs.employment')}
        </DrawerTabButton>
        <DrawerTabButton
          active={tab === 'account'}
          onClick={() => setTab('account')}
          testId="create-tab-account"
        >
          {t('tenant.employees.createModal.tabs.account')}
        </DrawerTabButton>
      </div>

      {tab === 'personal' && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label={t('tenant.employees.field.firstName')}
            required
            value={form.firstName}
            onChange={(e) => setForm((s) => ({ ...s, firstName: e.target.value }))}
            data-testid="employee-create-firstName"
          />
          <Input
            label={t('tenant.employees.field.lastName')}
            required
            value={form.lastName}
            onChange={(e) => setForm((s) => ({ ...s, lastName: e.target.value }))}
            data-testid="employee-create-lastName"
          />
          <Input
            label={t('tenant.employees.field.middleName')}
            value={form.middleName}
            onChange={(e) => setForm((s) => ({ ...s, middleName: e.target.value }))}
          />
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-text-secondary">
              {t('tenant.employees.field.birthDate')}
            </label>
            <DatePicker
              value={form.birthDate || ''}
              onChange={(v) => setForm((s) => ({ ...s, birthDate: v }))}
            />
          </div>
          <Input
            label={t('tenant.employees.field.pesel')}
            value={form.pesel}
            onChange={(e) =>
              setForm((s) => ({ ...s, pesel: e.target.value.replace(/\D/g, '').slice(0, 11) }))
            }
            placeholder="11 cyfr"
            data-testid="employee-create-pesel"
          />
          <Input
            label={t('tenant.employees.field.taxId')}
            value={form.taxId}
            onChange={(e) => setForm((s) => ({ ...s, taxId: e.target.value }))}
          />
          <FormField label={t('tenant.employees.field.idDocumentType')}>
            <Dropdown
              value={form.idDocumentType}
              onChange={(v) =>
                setForm((s) => ({ ...s, idDocumentType: v as EmployeeIdDocumentType }))
              }
              options={[
                { value: '', label: '—' },
                ...EMPLOYEE_ID_DOCUMENT_TYPES.map((typ) => ({
                  value: typ,
                  label: employeeIdDocumentTypeLabel(typ, t),
                })),
              ]}
            />
          </FormField>
          <Input
            label={t('tenant.employees.field.idDocumentNumber')}
            value={form.idDocumentNumber}
            onChange={(e) => setForm((s) => ({ ...s, idDocumentNumber: e.target.value }))}
          />
        </div>
      )}

      {tab === 'employment' && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField label={t('tenant.employees.field.company')} required>
            <Dropdown
              value={form.companyId}
              onChange={(v) => setForm((s) => ({ ...s, companyId: v, orgUnitId: '' }))}
              data-testid="employee-create-company"
              options={[
                { value: '', label: '—' },
                ...companies.map((c) => ({ value: c.id, label: c.shortName ?? c.legalName })),
              ]}
            />
          </FormField>
          <FormField
            label={t('tenant.employees.field.orgUnit')}
            hint={!form.companyId ? t('tenant.employees.filters.orgUnitHint') : undefined}
          >
            <Dropdown
              value={form.orgUnitId}
              disabled={!form.companyId}
              onChange={(v) => setForm((s) => ({ ...s, orgUnitId: v }))}
              data-testid="employee-create-org-unit"
              options={[
                { value: '', label: '—' },
                ...buildIndentedOrgUnitOptions(orgUnitsQ.data?.orgUnits ?? []),
              ]}
            />
          </FormField>
          <PositionCodeDropdown
            value={form.positionCode}
            onChange={(v) => setForm((s) => ({ ...s, positionCode: v }))}
          />
          <Input
            label={t('tenant.employees.field.specialization')}
            value={form.specialization}
            onChange={(e) => setForm((s) => ({ ...s, specialization: e.target.value }))}
          />
          <FormField label={t('tenant.employees.field.employmentType')}>
            <Dropdown
              value={form.employmentType}
              onChange={(v) => setForm((s) => ({ ...s, employmentType: v as EmploymentType }))}
              options={[
                { value: '', label: '—' },
                ...EMPLOYMENT_TYPES.map((typ) => ({
                  value: typ,
                  label: employmentTypeLabel(typ, t),
                })),
              ]}
            />
          </FormField>
          <Input
            label={t('tenant.employees.field.internalCode')}
            value={form.internalCode}
            onChange={(e) => setForm((s) => ({ ...s, internalCode: e.target.value }))}
          />
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-text-secondary">
              {t('tenant.employees.field.employmentStartDate')}
            </label>
            <DatePicker
              value={form.employmentStartDate || ''}
              onChange={(v) => setForm((s) => ({ ...s, employmentStartDate: v }))}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-text-secondary">
              {t('tenant.employees.field.employmentEndDate')}
            </label>
            <DatePicker
              value={form.employmentEndDate || ''}
              onChange={(v) => setForm((s) => ({ ...s, employmentEndDate: v }))}
            />
          </div>
          <Input
            label={t('tenant.employees.field.phone')}
            value={form.phone}
            onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
          />
          <Input
            label={t('tenant.employees.field.mobile')}
            value={form.mobile}
            onChange={(e) => setForm((s) => ({ ...s, mobile: e.target.value }))}
          />
          <Input
            label={t('tenant.employees.field.workEmail')}
            type="email"
            value={form.workEmail}
            onChange={(e) => setForm((s) => ({ ...s, workEmail: e.target.value }))}
          />
        </div>
      )}

      {tab === 'account' && (
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.createUser}
              onChange={(e) => setForm((s) => ({ ...s, createUser: e.target.checked }))}
              data-testid="employee-create-toggle-account"
            />
            <span className="font-medium">{t('tenant.employees.createModal.account.toggle')}</span>
          </label>
          <p className="text-xs text-text-tertiary">
            {t('tenant.employees.createModal.account.hint')}
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label={t('tenant.employees.createModal.account.email')}
              type="email"
              value={form.userEmail}
              onChange={(e) => setForm((s) => ({ ...s, userEmail: e.target.value }))}
              disabled={!form.createUser}
              placeholder={form.workEmail || ''}
              hint={
                form.createUser && !form.userEmail
                  ? t('tenant.employees.createModal.account.emailFallback')
                  : undefined
              }
              data-testid="employee-create-account-email"
            />
            <Input
              label={t('tenant.employees.createModal.account.displayName')}
              value={form.userDisplayName}
              onChange={(e) => setForm((s) => ({ ...s, userDisplayName: e.target.value }))}
              disabled={!form.createUser}
            />
            <FormField label={t('tenant.employees.createModal.account.language')}>
              <Dropdown
                value={form.userLanguage}
                onChange={(v) => setForm((s) => ({ ...s, userLanguage: v as RecordLanguage }))}
                disabled={!form.createUser}
                options={RECORD_LANGUAGES.map((code) => ({
                  value: code,
                  label: recordLanguageLabel(code, t),
                }))}
              />
            </FormField>
            <FormField label={t('tenant.employees.createModal.account.role')}>
              <Dropdown
                value={form.userRoleTemplateId}
                onChange={(v) => setForm((s) => ({ ...s, userRoleTemplateId: v }))}
                disabled={!form.createUser}
                data-testid="employee-create-account-role"
                options={[
                  { value: '', label: '—' },
                  ...(roleTemplatesQ.data?.roleTemplates ?? []).map((r) => ({
                    value: r.id,
                    label: r.name,
                  })),
                ]}
              />
            </FormField>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ============================================================================
// Modale akcji
// ============================================================================

function AnonymizeEmployeeModal({
  employee,
  loading,
  onClose,
  onConfirm,
}: {
  employee: EmployeeListItem;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={t('tenant.employees.anonymizeModal.title')}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={loading}
            data-testid="employee-anonymize-confirm"
          >
            {t('tenant.employees.anonymizeModal.confirm')}
          </Button>
        </div>
      }
    >
      <p className="mb-3 text-sm">
        {t('tenant.employees.anonymizeModal.body', {
          name: `${employee.firstName} ${employee.lastName}`,
        })}
      </p>
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
        {t('tenant.employees.anonymizeModal.warning')}
      </div>
    </Modal>
  );
}

function DeleteEmployeeModal({
  employee,
  loading,
  onClose,
  onConfirm,
}: {
  employee: EmployeeListItem;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={t('tenant.employees.deleteModal.title')}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={loading}
            data-testid="employee-delete-confirm"
          >
            {t('tenant.employees.deleteModal.confirm')}
          </Button>
        </div>
      }
    >
      <p className="text-sm">
        {t('tenant.employees.deleteModal.body', {
          name: `${employee.firstName} ${employee.lastName}`,
        })}
      </p>
    </Modal>
  );
}

// ============================================================================
// EditEmployeeModal — edycja danych pracownika (PATCH)
// ============================================================================

interface EditEmployeeForm {
  firstName: string;
  lastName: string;
  middleName: string;
  birthDate: string;
  pesel: string;
  taxId: string;
  idDocumentType: EmployeeIdDocumentType | '';
  idDocumentNumber: string;
  orgUnitId: string;
  positionCode: string;
  specialization: string;
  employmentType: EmploymentType | '';
  employmentStartDate: string;
  employmentEndDate: string;
  internalCode: string;
  phone: string;
  mobile: string;
  workEmail: string;
  isActive: boolean;
}

function employeeDetailToEditForm(emp: EmployeeDetail): EditEmployeeForm {
  return {
    firstName: emp.firstName,
    lastName: emp.lastName,
    middleName: emp.middleName ?? '',
    birthDate: emp.birthDate ?? '',
    pesel: '',
    taxId: emp.taxId ?? '',
    idDocumentType: (emp.idDocumentType as EmployeeIdDocumentType) ?? '',
    idDocumentNumber: '',
    orgUnitId: emp.orgUnitId ?? '',
    positionCode: emp.positionCode ?? '',
    specialization: emp.specialization ?? '',
    employmentType: (emp.employmentType as EmploymentType) ?? '',
    employmentStartDate: emp.employmentStartDate ?? '',
    employmentEndDate: emp.employmentEndDate ?? '',
    internalCode: emp.internalCode ?? '',
    phone: emp.phone ?? '',
    mobile: emp.mobile ?? '',
    workEmail: emp.workEmail ?? '',
    isActive: emp.isActive,
  };
}

function EditEmployeeModal({
  employee,
  companies,
  loading,
  onClose,
  onSave,
}: {
  employee: EmployeeDetail;
  companies: Array<{ id: string; legalName: string; shortName: string | null }>;
  loading: boolean;
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<EditModalTab>('personal');
  const [form, setForm] = useState<EditEmployeeForm>(() => employeeDetailToEditForm(employee));

  const orgUnitsQ = useOrgUnitsForCompany(employee.companyId);
  const companyName =
    companies.find((c) => c.id === employee.companyId)?.shortName ??
    companies.find((c) => c.id === employee.companyId)?.legalName ??
    '—';

  function buildPatchBody(): Record<string, unknown> {
    const body: Record<string, unknown> = {};
    if (form.firstName !== employee.firstName) body.firstName = form.firstName;
    if (form.lastName !== employee.lastName) body.lastName = form.lastName;
    body.middleName = form.middleName || null;
    body.birthDate = form.birthDate || null;
    body.taxId = form.taxId || null;
    body.idDocumentType = form.idDocumentType || null;
    body.orgUnitId = form.orgUnitId || null;
    body.positionCode = form.positionCode || null;
    body.specialization = form.specialization || null;
    body.employmentType = form.employmentType || null;
    body.employmentStartDate = form.employmentStartDate || null;
    body.employmentEndDate = form.employmentEndDate || null;
    body.internalCode = form.internalCode || null;
    body.phone = form.phone || null;
    body.mobile = form.mobile || null;
    body.workEmail = form.workEmail || null;
    body.isActive = form.isActive;
    // Dane wrażliwe — wysyłaj tylko gdy user coś wpisał
    if (form.pesel) body.pesel = form.pesel;
    if (form.idDocumentNumber) body.idDocumentNumber = form.idDocumentNumber;
    return body;
  }

  const canSubmit = !!form.firstName && !!form.lastName;

  return (
    <Modal
      open
      onClose={onClose}
      title={`${t('tenant.employees.editModal.title')} — ${employee.lastName} ${employee.firstName}`}
      size="xl"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => onSave(buildPatchBody())}
            disabled={!canSubmit || loading}
            data-testid="employee-edit-submit"
          >
            {t('common.save')}
          </Button>
        </div>
      }
    >
      <div role="tablist" className="mb-4 flex gap-2 border-b border-border-primary">
        <DrawerTabButton
          active={tab === 'personal'}
          onClick={() => setTab('personal')}
          testId="edit-tab-personal"
        >
          {t('tenant.employees.editModal.tabs.personal')}
        </DrawerTabButton>
        <DrawerTabButton
          active={tab === 'employment'}
          onClick={() => setTab('employment')}
          testId="edit-tab-employment"
        >
          {t('tenant.employees.editModal.tabs.employment')}
        </DrawerTabButton>
      </div>

      {tab === 'personal' && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label={t('tenant.employees.field.firstName')}
            required
            value={form.firstName}
            onChange={(e) => setForm((s) => ({ ...s, firstName: e.target.value }))}
            data-testid="employee-edit-firstName"
          />
          <Input
            label={t('tenant.employees.field.lastName')}
            required
            value={form.lastName}
            onChange={(e) => setForm((s) => ({ ...s, lastName: e.target.value }))}
            data-testid="employee-edit-lastName"
          />
          <Input
            label={t('tenant.employees.field.middleName')}
            value={form.middleName}
            onChange={(e) => setForm((s) => ({ ...s, middleName: e.target.value }))}
          />
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-text-secondary">
              {t('tenant.employees.field.birthDate')}
            </label>
            <DatePicker
              value={form.birthDate || ''}
              onChange={(v) => setForm((s) => ({ ...s, birthDate: v }))}
            />
          </div>
          <Input
            label={t('tenant.employees.field.pesel')}
            value={form.pesel}
            onChange={(e) =>
              setForm((s) => ({ ...s, pesel: e.target.value.replace(/\D/g, '').slice(0, 11) }))
            }
            placeholder="Zostaw puste żeby nie zmieniać"
            hint="Wypełnij tylko jeśli chcesz nadpisać istniejący PESEL"
            data-testid="employee-edit-pesel"
          />
          <Input
            label={t('tenant.employees.field.taxId')}
            value={form.taxId}
            onChange={(e) => setForm((s) => ({ ...s, taxId: e.target.value }))}
          />
          <FormField label={t('tenant.employees.field.idDocumentType')}>
            <Dropdown
              value={form.idDocumentType}
              onChange={(v) =>
                setForm((s) => ({ ...s, idDocumentType: v as EmployeeIdDocumentType }))
              }
              options={[
                { value: '', label: '—' },
                ...EMPLOYEE_ID_DOCUMENT_TYPES.map((typ) => ({
                  value: typ,
                  label: employeeIdDocumentTypeLabel(typ, t),
                })),
              ]}
            />
          </FormField>
          <Input
            label={t('tenant.employees.field.idDocumentNumber')}
            value={form.idDocumentNumber}
            onChange={(e) => setForm((s) => ({ ...s, idDocumentNumber: e.target.value }))}
            placeholder="Zostaw puste żeby nie zmieniać"
          />
        </div>
      )}

      {tab === 'employment' && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {/* Firma — tylko do odczytu (PATCH nie zmienia companyId) */}
          <div className="sm:col-span-2 rounded-lg border border-border-primary bg-bg-tertiary px-3 py-2 text-sm">
            <span className="text-text-tertiary">{t('tenant.employees.field.company')}: </span>
            <span className="font-medium text-text-primary">{companyName}</span>
          </div>
          <FormField label={t('tenant.employees.field.orgUnit')}>
            <Dropdown
              value={form.orgUnitId}
              onChange={(v) => setForm((s) => ({ ...s, orgUnitId: v }))}
              data-testid="employee-edit-org-unit"
              options={[
                { value: '', label: '—' },
                ...buildIndentedOrgUnitOptions(orgUnitsQ.data?.orgUnits ?? []),
              ]}
            />
          </FormField>
          <PositionCodeDropdown
            value={form.positionCode}
            onChange={(v) => setForm((s) => ({ ...s, positionCode: v }))}
          />
          <Input
            label={t('tenant.employees.field.specialization')}
            value={form.specialization}
            onChange={(e) => setForm((s) => ({ ...s, specialization: e.target.value }))}
          />
          <FormField label={t('tenant.employees.field.employmentType')}>
            <Dropdown
              value={form.employmentType}
              onChange={(v) => setForm((s) => ({ ...s, employmentType: v as EmploymentType }))}
              options={[
                { value: '', label: '—' },
                ...EMPLOYMENT_TYPES.map((typ) => ({
                  value: typ,
                  label: employmentTypeLabel(typ, t),
                })),
              ]}
            />
          </FormField>
          <Input
            label={t('tenant.employees.field.internalCode')}
            value={form.internalCode}
            onChange={(e) => setForm((s) => ({ ...s, internalCode: e.target.value }))}
          />
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-text-secondary">
              {t('tenant.employees.field.employmentStartDate')}
            </label>
            <DatePicker
              value={form.employmentStartDate || ''}
              onChange={(v) => setForm((s) => ({ ...s, employmentStartDate: v }))}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-text-secondary">
              {t('tenant.employees.field.employmentEndDate')}
            </label>
            <DatePicker
              value={form.employmentEndDate || ''}
              onChange={(v) => setForm((s) => ({ ...s, employmentEndDate: v }))}
            />
          </div>
          <Input
            label={t('tenant.employees.field.phone')}
            value={form.phone}
            onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
          />
          <Input
            label={t('tenant.employees.field.mobile')}
            value={form.mobile}
            onChange={(e) => setForm((s) => ({ ...s, mobile: e.target.value }))}
          />
          <Input
            label={t('tenant.employees.field.workEmail')}
            type="email"
            value={form.workEmail}
            onChange={(e) => setForm((s) => ({ ...s, workEmail: e.target.value }))}
          />
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((s) => ({ ...s, isActive: e.target.checked }))}
              data-testid="employee-edit-isActive"
            />
            {t('tenant.employees.field.isActive')} ({t('tenant.employees.isActive.yes')})
          </label>
        </div>
      )}
    </Modal>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function detailToListItem(emp: EmployeeDetail, hasAccount: boolean): EmployeeListItem {
  return {
    id: emp.id,
    firstName: emp.firstName,
    lastName: emp.lastName,
    middleName: emp.middleName,
    birthDate: emp.birthDate,
    taxId: emp.taxId,
    idDocumentType: emp.idDocumentType,
    pesel: null,
    idDocumentNumber: null,
    companyId: emp.companyId,
    orgUnitId: emp.orgUnitId,
    positionCode: emp.positionCode,
    specialization: emp.specialization,
    employmentType: emp.employmentType,
    employmentStartDate: emp.employmentStartDate,
    employmentEndDate: emp.employmentEndDate,
    internalCode: emp.internalCode,
    managerEmployeeId: emp.managerEmployeeId,
    phone: emp.phone,
    mobile: emp.mobile,
    workEmail: emp.workEmail,
    isActive: emp.isActive,
    hasAccount,
    createdAt: emp.createdAt,
    gcrecord: emp.gcrecord,
  };
}

// Reference ORG_UNITS_BY_COMPANY_KEY to keep import side-effect-clean.
void ORG_UNITS_BY_COMPANY_KEY;
void EMPLOYEES_LIST_KEY;
